// Alarm DISPLAY vocabulary for the cloud web Alarms page (Phase UI-1).
//
// The web build does NOT depend on `@neomaris/protocol` at runtime (protocol is
// TYPE-ONLY in cloud/web), so the pure presentational vocabulary the page needs —
// severity rank/label, the six-value alert-state meta, `isSurfacedState`,
// `sortActiveForDisplay`, `formatCountdown` — is MIRRORED here from
// `packages/protocol/src/alarm-view.ts`. `alarmDisplay.test.ts` imports that
// protocol source by relative path and asserts the two stay byte-identical in
// behaviour (the same parity convention as `networkRecommendation.test.ts`).
//
// On top of that mirror this module adds the web-only pieces: the TypeScript
// mirrors of the §5.7 `GET /alerts/state` response shapes, and the page's own
// derivation/formatting helpers (`formatModeSummary`, `formatTransition`, the
// active/configured row views, the nav-badge tone). None of the web-only helpers
// are part of the parity block.

import {
  describeRuntimeDisabledReason,
  type UnitSettings,
  type VesselType,
} from '@neomaris/protocol';
import {
  KPA_TO_HPA,
  LIFE_SAFETY_RECIPE_BASES,
  isContactStateParam,
  TEMPORARY_SILENCE_PERIOD_SECONDS_BY_SEVERITY,
  foldModesToCanonical,
  parseRecipeId,
} from '@neomaris/rules';

import {
  conditionsClause,
  convertedQuantity,
  customAlarmSentence,
  formatDuration,
  formatThreshold as formatValueWithUnit,
  readConditions,
  type ConditionClauseSpec,
  type CustomSentenceSpec,
} from './alarmSentence';
import { categoryFor, roundedReading, type UnitCategory } from './displayUnits';
import { signalLabel, type TriggerClass } from './signalCatalog';
import {
  WATCH_PRESETS,
  canonicalWatchModes,
  detectWatchPreset,
  watchGapPhrase,
  watchPresetGap,
  watchPresets,
} from './watchSchedule';

// ---------------------------------------------------------------------------
// Mirrored from @neomaris/protocol alarm-view — severity.
// ---------------------------------------------------------------------------

/** How urgent an alarm is, severity-ordered emergency → caution. */
export type AlarmSeverity = 'emergency' | 'alarm' | 'warning' | 'caution';

/** The severities in urgency order (index 0 = most urgent). */
export const ALARM_SEVERITIES: readonly AlarmSeverity[] = [
  'emergency',
  'alarm',
  'warning',
  'caution',
];

/**
 * Rank a severity for ascending sorts (0 = most urgent). An unrecognized value
 * ranks last so a malformed row never floats to the top of a "most urgent first"
 * list.
 */
export function severityRank(severity: AlarmSeverity): number {
  const index = ALARM_SEVERITIES.indexOf(severity);
  return index === -1 ? ALARM_SEVERITIES.length : index;
}

/** The badge label for an alarm severity (emergency → caution). */
export function alarmSeverityLabel(severity: AlarmSeverity): string {
  switch (severity) {
    case 'emergency':
      return 'Emergency';
    case 'alarm':
      return 'Alarm';
    case 'warning':
      return 'Warning';
    case 'caution':
      return 'Caution';
    default:
      return 'Alarm';
  }
}

// ---------------------------------------------------------------------------
// Severity, in the operator's terms (web-only — NOT part of the parity block).
// ---------------------------------------------------------------------------

/**
 * The one line under the severity picker saying what choosing this one does.
 *
 * "Emergency" and "Alarm" are two words in a dropdown and the difference
 * between them is entirely behavioural, so the copy states the BEHAVIOUR — and
 * only behaviour the code actually implements:
 *
 *  * emergency — `N2K_ALERT_TYPE.emergency` = 1 with alert priority 10, the top
 *    of the ladder every MFD on the bus sorts by (`@neomaris/rules`
 *    `n2k-identity.ts`), and the shortest temporary silence there is: 60 s
 *    against 300/600 (`TEMPORARY_SILENCE_PERIOD_SECONDS_BY_SEVERITY`).
 *  * alarm — reactivation 1800 s: acknowledging buys half an hour, then it is
 *    back while the condition holds.
 *  * warning — reactivation 0: acknowledged means acknowledged.
 *  * caution — reactivation 0 AND the one severity that skips Awaiting
 *    Acknowledge entirely (`@neomaris/alarm-lifecycle` `state-machine.ts`
 *    `rule.isCaution`, per NMEA 2000 Appendix D §9.6.1.2), so it disappears on
 *    its own when the condition passes.
 *
 * What it deliberately does NOT claim: which channels an alarm reaches. That is
 * per-definition `notificationUrgencyByMode`, not a function of severity, so
 * "sounds everywhere" / "never wakes you" would be a promise nothing keeps.
 */
export function severityDescription(severity: AlarmSeverity): string {
  switch (severity) {
    case 'emergency':
      return 'Danger to the boat or crew — the most urgent level, and it cannot be silenced for long.';
    case 'alarm':
      return 'Needs action now.';
    case 'warning':
      return 'Needs attention soon.';
    case 'caution':
      return 'Good to know — it clears itself once the condition passes.';
    default:
      return 'Needs action now.';
  }
}

// ---------------------------------------------------------------------------
// Mirrored from @neomaris/protocol alarm-view — alert lifecycle state.
// ---------------------------------------------------------------------------

/** The six-value Neomaris Monitor alert lifecycle state. */
export type AlertState =
  | 'Disabled'
  | 'Normal'
  | 'Active'
  | 'Silenced'
  | 'Acknowledged'
  | 'AwaitingAcknowledge';

/** A coarse visual "tone" for an alert state, mapped by each surface onto its palette. */
export type AlertStateTone = 'danger' | 'attention' | 'hushed' | 'ok' | 'off';

/** Presentation metadata for one alert state. */
export interface AlertStateMeta {
  /** Human-readable label for the state pill. */
  readonly label: string;
  /** Coarse visual tone the surface maps to its own palette. */
  readonly tone: AlertStateTone;
  /** Whether this state should draw the operator's eye (drives sort / badges). */
  readonly needsAttention: boolean;
}

/**
 * Per-state display metadata. `AwaitingAcknowledge` — the condition cleared but
 * the operator still has to acknowledge it — reads "Back to normal — tap to
 * acknowledge" in amber (`attention`) so it is visually distinct from an
 * actively-firing `Active` alert (red / `danger`) and from a handled one.
 *
 * The label says what HAPPENED and then what to do about it. "Cleared — needs
 * acknowledge" said neither: "cleared" reads like the alarm is over and gone
 * (it is the word every other system uses for dismissed), and "needs
 * acknowledge" names a state rather than an action, so the amber row looked
 * like a fault in the panel rather than a row waiting on one tap.
 */
export const ALERT_STATE_META: Record<AlertState, AlertStateMeta> = {
  Disabled: { label: 'Disabled', tone: 'off', needsAttention: false },
  Normal: { label: 'Normal', tone: 'ok', needsAttention: false },
  Active: { label: 'Active', tone: 'danger', needsAttention: true },
  Silenced: { label: 'Silenced', tone: 'hushed', needsAttention: true },
  Acknowledged: {
    label: 'Acknowledged',
    tone: 'hushed',
    needsAttention: false,
  },
  AwaitingAcknowledge: {
    label: 'Back to normal — tap to acknowledge',
    tone: 'attention',
    needsAttention: true,
  },
};

/**
 * Whether a state should be SURFACED in an "active alerts" list. Everything
 * except a quiescent `Normal` and a not-evaluating `Disabled` alert is worth
 * showing.
 */
export function isSurfacedState(state: AlertState): boolean {
  return state !== 'Normal' && state !== 'Disabled';
}

// ---------------------------------------------------------------------------
// Mirrored from @neomaris/protocol alarm-view — action gates.
// Part of the parity block: `alarmDisplay.test.ts` asserts these stay
// byte-identical in behaviour to the protocol source.
// ---------------------------------------------------------------------------

/**
 * Whether an Acknowledge action is meaningful in this state. An alert can be
 * acknowledged while it is `Active`, temporarily `Silenced`, or waiting in
 * `AwaitingAcknowledge` (condition cleared, ack still pending).
 */
export function canAck(state: AlertState): boolean {
  return state === 'Active' || state === 'Silenced' || state === 'AwaitingAcknowledge';
}

/**
 * Whether a temporary-Silence action is meaningful in this state. Only an
 * actively-firing `Active` alert can be silenced (silencing an already-silenced
 * or acknowledged alert is a no-op the UI should not offer).
 */
export function canSilence(state: AlertState): boolean {
  return state === 'Active';
}

// ---------------------------------------------------------------------------
// Provenance attribution gate (web-only presentation, NOT a parity mirror).
// ---------------------------------------------------------------------------

/**
 * Whether a landed state / transition is a human `Acknowledged` or `Silenced` —
 * the only two states for which a runtime snapshot's `acknowledgeSource` is a
 * CURRENT, honest attribution. The boat evaluator sets `acknowledgeSource` on
 * ack/silence and NEVER clears it, so every LATER automatic transition (e.g. a
 * re-fire `Normal → Active`) still carries the stale human source. Provenance
 * consumers gate on this so an automatic row is never falsely attributed to the
 * human who last acked — a safety-audit-trail correctness property. Takes a raw
 * `string` so it serves both a wire `transition` and a typed {@link AlertState}.
 */
export function isAckOrSilenceState(state: string | undefined): boolean {
  return state === 'Acknowledged' || state === 'Silenced';
}

// ---------------------------------------------------------------------------
// Mirrored from @neomaris/protocol alarm-view — ordering + countdown helpers.
// ---------------------------------------------------------------------------

/** The minimal shape {@link sortActiveForDisplay} needs from a row. */
export interface DisplayAlertRow {
  /** Optional severity; rows without one sort after every severity-bearing row. */
  readonly severity?: AlarmSeverity;
  /** ISO wall-clock of the last state transition; newest wins the tiebreak. */
  readonly lastTransitionAt?: string;
}

/**
 * Order alerts for display: most-urgent severity first, then most-recently
 * transitioned first. Returns a NEW array — never mutates the input.
 */
export function sortActiveForDisplay<T extends DisplayAlertRow>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => {
    const rankA = a.severity ? severityRank(a.severity) : ALARM_SEVERITIES.length;
    const rankB = b.severity ? severityRank(b.severity) : ALARM_SEVERITIES.length;
    if (rankA !== rankB) return rankA - rankB;
    return parseTimeMs(b.lastTransitionAt) - parseTimeMs(a.lastTransitionAt);
  });
}

function parseTimeMs(iso: string | undefined): number {
  if (!iso) return 0;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : 0;
}

/**
 * Format the time remaining until a deadline as `H h M m` / `M m S s` / `S s`
 * (e.g. `8 m 12 s`). An absent / unparseable deadline is the em dash; a deadline
 * in the past is `0 s`. Pure over an injected `nowMs` so a live 1 Hz countdown is
 * unit-tested without a clock.
 */
export function formatCountdown(deadlineIso: string | undefined, nowMs: number): string {
  if (!deadlineIso) return '—';
  const deadline = Date.parse(deadlineIso);
  if (!Number.isFinite(deadline)) return '—';
  const remainingSec = Math.max(0, Math.ceil((deadline - nowMs) / 1000));
  const hours = Math.floor(remainingSec / 3600);
  const minutes = Math.floor((remainingSec % 3600) / 60);
  const seconds = remainingSec % 60;
  if (hours > 0) return `${hours} h ${minutes} m`;
  if (minutes > 0) return `${minutes} m ${seconds} s`;
  return `${seconds} s`;
}

/**
 * The numeric threshold fields a threshold map exposes — the subset whose values
 * are finite numbers. Returned name→value sorted by name for a stable render
 * order. Mirrors the protocol `numericThresholdFields`.
 */
export function numericThresholdFields(
  thresholds: Record<string, unknown>
): { key: string; value: number }[] {
  const fields: { key: string; value: number }[] = [];
  for (const [key, raw] of Object.entries(thresholds)) {
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      fields.push({ key, value: raw });
    }
  }
  fields.sort((a, b) => a.key.localeCompare(b.key));
  return fields;
}

// ---------------------------------------------------------------------------
// §5.7 response shapes (GET /v1/boats/:boatId/alerts/state) — web mirrors.
// ---------------------------------------------------------------------------

/**
 * The runtime snapshot carried verbatim inside `runtime.payload.runtime` — the
 * boat's `AlertRuntimeStateSnapshot` (`core/src/monitoring/evaluator`). The wire
 * shape uses `silenceExpiresAt`/`reactivationExpiresAt` + `currentValue`; the
 * accessors below ALSO read the `timers.*`/`lastValue` shape the boat-local
 * `AlertRuntimeView` uses, so this page renders correctly whichever the mirror
 * passed through. Loosely typed (extra keys allowed) — it is wire data.
 */
export interface AlarmRuntimeSnapshot {
  readonly state?: string;
  readonly occurrenceNumber?: number;
  readonly whatHappened?: string;
  readonly acknowledgeSource?: string;
  readonly acknowledgeSourceNetworkId?: string;
  readonly currentValue?: unknown;
  readonly lastValue?: unknown;
  readonly silenceExpiresAt?: string;
  readonly reactivationExpiresAt?: string;
  readonly runtimeDisabledReason?: string;
  readonly timers?: AlarmTimerDeadlines;
  readonly [key: string]: unknown;
}

/** Outstanding lifecycle deadlines as wall-clock ISO strings (boat-local shape). */
export interface AlarmTimerDeadlines {
  readonly setAt?: string;
  readonly clearAt?: string;
  readonly silenceUntil?: string;
  readonly reactivationAt?: string;
}

/** The mirrored `{ from, reason, runtime }` payload (verbatim from the boat). */
export interface AlarmRuntimePayload {
  readonly from?: string;
  readonly reason?: string;
  readonly runtime?: AlarmRuntimeSnapshot;
  readonly [key: string]: unknown;
}

/** The mirrored `alert_states` row for one definition (null ⇒ not yet reported). */
export interface AlarmRuntimeView {
  readonly state: string;
  readonly occurrenceNumber: number;
  readonly lastTransitionAt: string;
  readonly payload: AlarmRuntimePayload | null;
}

/** One ACTIVE definition's stored content (the §5.7 `definition` object). */
export interface AlarmDefView {
  readonly id: string;
  readonly recipeId: string;
  readonly title: string;
  readonly severity: AlarmSeverity | null;
  readonly enabled: boolean;
  readonly enabledModes: string[];
  readonly thresholdsByMode: Record<string, Record<string, unknown>>;
  readonly notificationUrgencyByMode: Record<string, unknown>;
  readonly origin: string | null;
  readonly compileStatus: string | null;
  readonly compileReason: string | null;
  /**
   * Optional human explanation of why this alarm exists (the recipe/AI rationale).
   * Not on the current §5.7 wire — read defensively so the detail page's "Why
   * this alarm" disclosure lights up if a future response carries it.
   */
  readonly explanation?: string;
  /**
   * The custom (non-recipe) spec — an object for a custom alarm, `null` for a
   * recipe one, absent from an older response. Read it through
   * {@link readCustomSpec}: like `explanation` this is wire data, and a row
   * written by a newer build (or a corrupt one) must degrade, never throw.
   */
  readonly custom?: AlarmCustomSpec | null;
  /**
   * The authored qualifier conditions (`{ logic, clauses }`) — `null` when the
   * alarm has none, absent from an older response. Deliberately `unknown`: read
   * it through `readConditions`, which drops clauses this build cannot parse
   * rather than rendering junk.
   */
  readonly conditions?: unknown;
  /**
   * The concrete-instance PIN (`{ path }`) — which engine/bank/tank this recipe
   * alarm watches — or `null` for "any" (the compiler's family first-match, and
   * every alarm authored before pins existed). Absent from an older response.
   * Deliberately `unknown` for the same reason as `conditions`: read it through
   * `readBindingPath`, which degrades a malformed block to "no pin" rather than
   * rendering junk.
   */
  readonly binding?: unknown;
  /**
   * True when the compiler DROPPED this recipe's engine-running preset because
   * the boat isn't reporting engine RPM (plan D2 — fail toward alarming: the
   * alarm is armed WITHOUT the condition rather than silently disarmed). The
   * surfaces must say so; see {@link CONDITION_UNBOUND_NOTE}.
   */
  readonly conditionUnbound?: boolean;
  /**
   * The ONE telemetry path this alarm actually watches, as the CLOUD's own
   * compile resolved it (ADR 0170) — derived per request from the boat's latest
   * compiled ruleset, never stored on the definition. `null` when no rule for
   * this definition exists in that ruleset (pending, disabled, or compiled
   * after it); ABSENT from a cloud that predates ADR 0170.
   *
   * Absent and null are different answers and {@link resolvedPathFor} treats
   * them differently — read it through there, never directly.
   */
  readonly resolvedPath?: string | null;
  /**
   * The compiled rule's RESOLVED condition-clause paths — an `engine_running`
   * preset appears here as the concrete `propulsion.<inst>.revolutions` it bound
   * to, and a preset the compiler dropped (D2) appears not at all. `[]` when the
   * rule carries no condition; absent from an older cloud. Wire data: read it
   * through {@link wireConditionPaths}.
   */
  readonly conditionPaths?: readonly string[];
  /**
   * The compiled rule's trigger in STORE units (ADR 0170) — the same domain live
   * samples arrive in, which is the whole point: `low_oil_pressure` is authored
   * in kPa and compiles to hPa, so only this number can be compared against a
   * live reading. `null` when there is no compiled rule; absent from an older
   * cloud. Read it through {@link readWireTrigger}.
   */
  readonly trigger?: AlarmWireTrigger | null;
}

/**
 * The `definition.trigger` block (§5.7, ADR 0170) — the compiled rule's class
 * and its two numeric trigger fields, in STORE units.
 *
 * `class` is a plain `string`, not a union, for the same reason
 * {@link AlarmCustomSpec.triggerClass} is: a newer compiler can emit a class
 * this build has never heard of, and {@link setpointPhrase} degrades to saying
 * nothing rather than inventing a threshold phrase for it.
 */
export interface AlarmWireTrigger {
  readonly class: string;
  readonly setpoint?: number;
  readonly clearpoint?: number;
}

/** One `alerts[]` entry: an active definition joined with its runtime state. */
export interface AlarmAlertRow {
  readonly definition: AlarmDefView;
  readonly runtime: AlarmRuntimeView | null;
}

/** The full `GET /alerts/state` response. */
export interface AlarmStateResponse {
  readonly alerts: AlarmAlertRow[];
  readonly rulesetVersion: number | null;
  readonly mirroredAt: string | null;
  /**
   * What kind of boat this is, MIRRORED from the boat's own answer (ADR 0173) —
   * `null` when nobody has asked it yet, which is most boats. It rides this read
   * because every alarm surface already subscribes to it.
   *
   * Optional on the wire so an older cloud (or a cached response) reads as
   * unknown rather than crashing the page. Unknown is the cautious branch
   * everywhere: full sailing disclosure, and the prompt card asking the
   * question.
   */
  readonly vesselType?: VesselType | null;
  /**
   * When the owner paused ALL alarm watching, MIRRORED from the boat's own
   * answer (ADR 0174) — `null` while the boat is watching, which is every boat
   * that has never been paused.
   *
   * It drives the banner and nothing else. Whether an individual alarm is
   * evaluating is already in that row's own runtime snapshot, which reports
   * `Disabled` with `runtimeDisabledReason: 'paused_by_owner'` while the pause
   * is in force — the rows read the BOAT, so a mirror that is a frame behind
   * cannot make a row claim something the boat did not say.
   *
   * Optional on the wire so an older cloud (or a cached response) reads as
   * watching rather than crashing the page. Reading absence as "watching" is the
   * right way round: the banner is a claim about the boat, and a claim we have
   * no evidence for should not be made.
   */
  readonly alarmsPausedAt?: string | null;
  /**
   * Whether what the cloud holds is actually IN EFFECT on the boat.
   *
   * Saving an alarm in the cloud and the boat running it are two different
   * facts, and this page used to show only the first. A boat whose ruleset
   * delivery is switched off never hears about an edit at all, so a toggle
   * flipped to "Disabled" here would render as done while the alarm kept
   * sounding on board — which is exactly what happened underway on 2026-08-14.
   *
   * Optional on the wire so an older cloud reads as "nothing to say" rather
   * than crashing the page.
   */
  readonly boatSync?: BoatSync | null;
}

/** The boat-sync facts behind {@link describeBoatSync}. */
export interface BoatSync {
  /** Newest ruleset version the BOAT confirmed applying. Null = never. */
  readonly appliedVersion: number | null;
  /** True only when the boat has confirmed the version the cloud holds. */
  readonly inSync: boolean;
  /** Whether cloud→boat alarm delivery is switched on for this boat. */
  readonly deliveryEnabled: boolean;
  /**
   * Whether the boat ended up with the same alarms, as distinct from having
   * received them. Absent on an older API, which reads as `'unknown'` and says
   * nothing — the behaviour before this field existed.
   */
  readonly contentState?: 'agreed' | 'pending' | 'diverged' | 'unknown';
}

/**
 * Plain-English standing state of "is the boat actually running this?", or
 * `null` when there is nothing worth saying (in sync, or no evidence either
 * way).
 *
 * Deliberately never prints the machine reason (`ruleset_delivery_enabled=false`
 * and friends) — those are for operators and logs.
 */
export function describeBoatSync(sync: BoatSync | null | undefined): string | null {
  if (sync === null || sync === undefined) return null;
  if (sync.inSync) return null;
  if (!sync.deliveryEnabled) {
    return 'This boat isn’t receiving alarm changes yet, so it’s still running its own settings.';
  }
  if (sync.appliedVersion === null) {
    return 'Your boat hasn’t confirmed these alarms yet — it will pick them up next time it connects.';
  }
  return 'Your boat is still running an earlier version of these alarms — it will catch up next time it connects.';
}

/**
 * The one case the sync line above cannot express: the boat is up to date and
 * still holding something different.
 *
 * Kept separate from {@link describeBoatSync} because it is a different kind of
 * fact. The sentences there are all "wait, it is coming"; this one is "waiting
 * will not fix it". It is also the only one that asks the owner to do anything,
 * so it must not fire on a boat that is merely behind — `contentAgreement`
 * upstream reports that as `pending`, not `diverged`.
 */
export function describeContentDivergence(sync: BoatSync | null | undefined): string | null {
  if (sync === null || sync === undefined) return null;
  // `inSync` is re-checked here even though `contentAgreement` upstream already
  // reports a behind boat as `pending`. This sentence contradicts the "it will
  // catch up" one, so the two must never be able to print together — and that
  // guarantee should not depend on a predicate in another package staying
  // correct.
  if (!sync.inSync) return null;
  return sync.contentState === 'diverged'
    ? 'Your boat is up to date but watching a different set of alarms than this list shows. Open an alarm to see what it is actually watching.'
    : null;
}

/**
 * What became of one edit's delivery — the transient half, read from a mutation
 * response. Mirrors `RulesetDeliveryState` in `cloud/src/core/alerts/push.ts`.
 */
export type DeliveryState = 'delivered' | 'staged' | 'not_delivered' | 'skipped' | 'unchanged';

/**
 * Plain-English outcome of a single save, or `null` when the ordinary "Saved."
 * wording already tells the truth.
 *
 * `staged` counts as reaching the boat: it was handed to the peer host that
 * holds the socket. `not_delivered` and `skipped` are the two that used to
 * report as success.
 */
export function describeDelivery(state: DeliveryState | undefined): string | null {
  switch (state) {
    case 'not_delivered':
      return 'Saved. Your boat is offline, so it hasn’t got this change yet — it will next time it connects.';
    case 'skipped':
      return 'Saved. This boat isn’t receiving alarm changes yet, so it’s still running its own settings.';
    default:
      return null;
  }
}

/**
 * What a DELETE did (ADR 0213). Mirrors `AlarmRemoval['outcome']` in
 * `cloud/src/core/alerts/edit.ts`: the cloud turns the alarm off on the boat
 * first, and removes it only once a live boat socket took that.
 */
export type AlarmRemovalOutcome = 'removed' | 'disabled_pending_removal';

/** The DELETE response, as far as a page reads it. */
export interface AlarmRemovalResponse {
  readonly outcome?: AlarmRemovalOutcome;
  readonly delivery?: { readonly state?: DeliveryState };
}

/**
 * The one sentence for a delete that did NOT remove the alarm, or `null` when
 * it did.
 *
 * A removal gets no sentence: the row leaving the list is the whole claim, and
 * the cloud only makes it once the boat has the alarm turned off. An absent
 * `outcome` (a cloud from before ADR 0213, which always retired) reads the same
 * way, and the page's re-read settles it either way.
 *
 * A kept alarm always says "not removed", because it is still in the list and
 * nothing will take it out on its own — the owner has to remove it again.
 * `skipped` gets its own sentence: every other state reaches the boat in the
 * end, but a boat that isn't receiving alarm changes keeps running this alarm
 * however long you wait.
 */
export function describeRemoval(res: AlarmRemovalResponse | null | undefined): string | null {
  if (res?.outcome !== 'disabled_pending_removal') return null;
  if (res.delivery?.state === 'skipped') {
    return 'Turned off here, but not removed: this boat isn’t receiving alarm changes yet, so it’s still running this alarm. It stays in your list.';
  }
  return 'Turned off, but not removed: your boat hasn’t taken the change yet, and will switch this alarm off as soon as it does. Remove it again once your boat is online to take it off your list.';
}

// ---------------------------------------------------------------------------
// Runtime-snapshot accessors (defensive over both wire shapes).
// ---------------------------------------------------------------------------

const ALERT_STATES: readonly AlertState[] = [
  'Disabled',
  'Normal',
  'Active',
  'Silenced',
  'Acknowledged',
  'AwaitingAcknowledge',
];

/** Coerce a wire state string to a known {@link AlertState} (unknown ⇒ Normal). */
export function asAlertState(state: string | undefined): AlertState {
  return state !== undefined && (ALERT_STATES as readonly string[]).includes(state)
    ? (state as AlertState)
    : 'Normal';
}

/** The runtime snapshot inside a runtime view's payload, if present. */
export function runtimeSnapshot(
  runtime: AlarmRuntimeView | null
): AlarmRuntimeSnapshot | undefined {
  return runtime?.payload?.runtime;
}

/** The silence deadline (either wire shape), if a silence timer is running. */
export function silenceDeadline(snap: AlarmRuntimeSnapshot | undefined): string | undefined {
  return snap?.timers?.silenceUntil ?? snap?.silenceExpiresAt;
}

/** The reactivation deadline (either wire shape), if a reactivation timer is running. */
export function reactivationDeadline(snap: AlarmRuntimeSnapshot | undefined): string | undefined {
  return snap?.timers?.reactivationAt ?? snap?.reactivationExpiresAt;
}

/** Whether a runtime view carries any live silence/reactivation countdown. */
export function hasLiveCountdown(runtime: AlarmRuntimeView | null): boolean {
  const snap = runtimeSnapshot(runtime);
  return silenceDeadline(snap) !== undefined || reactivationDeadline(snap) !== undefined;
}

/**
 * The ack-provenance line for a row, e.g. `Acked from cloud · alarms-verify`.
 * `undefined` when no `acknowledgeSource` is set (never acknowledged). Mirrors
 * the boat-local admin `ackProvenance`.
 */
export function ackProvenance(
  source: string | undefined,
  networkId: string | undefined
): string | undefined {
  if (source === undefined || source.length === 0) return undefined;
  const who =
    networkId !== undefined && networkId.trim().length > 0 ? ` · ${networkId.trim()}` : '';
  return `Acked from ${source}${who}`;
}

const ORDINAL_SUFFIX_EXCEPTIONS = new Set([11, 12, 13]);

/** English ordinal for a positive integer (1 → "1st", 3 → "3rd", 12 → "12th"). */
export function ordinal(n: number): string {
  const abs = Math.abs(Math.trunc(n));
  const lastTwo = abs % 100;
  const last = abs % 10;
  let suffix = 'th';
  if (!ORDINAL_SUFFIX_EXCEPTIONS.has(lastTwo)) {
    if (last === 1) suffix = 'st';
    else if (last === 2) suffix = 'nd';
    else if (last === 3) suffix = 'rd';
  }
  return `${n}${suffix}`;
}

/**
 * The occurrence line for an active row: `3rd time` for a repeat firing. The
 * first occurrence returns `undefined` (a "1st time" line is noise), so the UI
 * shows it only when the alarm has fired before.
 */
export function occurrenceLabel(occurrenceNumber: number | undefined): string | undefined {
  if (occurrenceNumber === undefined || !Number.isFinite(occurrenceNumber)) return undefined;
  const n = Math.trunc(occurrenceNumber);
  if (n <= 1) return undefined;
  return `${ordinal(n)} time`;
}

/**
 * Humanize a lifecycle transition into a one-line "what happened" fallback when
 * the runtime snapshot carries no `whatHappened`. `reason` is an evaluator event
 * type (e.g. `SET_DELAY_FIRED`) rendered as lower-case words.
 */
export function formatTransition(entry: { from?: string; to?: string; reason?: string }): string {
  const arrow =
    entry.from && entry.to ? `${entry.from} → ${entry.to}` : (entry.to ?? entry.from ?? 'Updated');
  const reason = humanizeReason(entry.reason);
  return reason ? `${arrow} · ${reason}` : arrow;
}

function humanizeReason(reason: string | undefined): string | undefined {
  if (reason === undefined || reason.trim() === '') return undefined;
  return reason.trim().toLowerCase().replace(/_/g, ' ');
}

// ---------------------------------------------------------------------------
// Per-mode threshold summary (formatModeSummary).
// ---------------------------------------------------------------------------

/**
 * How ONE stored threshold number is written out. `plain` prints it in the
 * account's unit for the quantity when it has one (ADR 0189) and verbatim in
 * its stored unit otherwise. `duration` and `percent` do NOT: a stored
 * `debounceMs` of 5000 is not a number anyone should have to divide, and
 * neither shape is a physical quantity a unit preference speaks to.
 */
type ThresholdShape = 'plain' | 'duration' | 'percent';

/**
 * The words around one recipe threshold key. Mirrors the `@neomaris/rules`
 * recipe param metadata for the keys the compiler reads, but phrased for a
 * SENTENCE fragment rather than a form legend — the read surfaces string these
 * together after the mode list ("Anchored, Underway · low 11.8 V, waits 5 s").
 *
 * The five duration keys and the two ratios used to print their raw stored
 * numbers here: `debounce 5000 ms`, `high 0.9`. Both are the storage domain
 * leaking onto a page an owner reads, which is what this vocabulary exists to
 * stop; they now go through {@link formatDuration} and a percent conversion.
 * Storage is untouched — nothing here is ever written back.
 *
 * An unknown key falls back to a humanized key name and no unit — hence "using
 * the recipe param labels WHERE AVAILABLE".
 */
interface ThresholdSpec {
  /** The words BEFORE the number. */
  readonly label: string;
  /** How the number itself reads. Absent ⇒ `plain`. */
  readonly shape?: ThresholdShape;
  /** The unit PRINTED after a `plain` number when nothing converts. */
  readonly unit?: string;
  /**
   * The unit the number is STORED in, when that differs from the glyph above.
   * Only the temperature params need it: they print `°C` but are stored as the
   * bare `C` the conversion tables key on, and passing the glyph would miss the
   * store-unit gate and silently never convert.
   */
  readonly storeUnit?: string;
  /**
   * What the number MEASURES (ADR 0189). Never written in the table below —
   * {@link thresholdSpecFor} fills it from {@link PARAM_UNIT_CATEGORY}, which
   * the edit boxes read the same mapping of.
   */
  readonly category?: UnitCategory;
  /** The words AFTER the number, for a phrase that brackets it. */
  readonly tail?: string;
}

const THRESHOLD_LABELS: Record<string, ThresholdSpec> = {
  voltsLow: { label: 'low', unit: 'V' },
  maxCelsius: { label: 'max', unit: '°C', storeUnit: 'C' },
  minKpa: { label: 'min', unit: 'kPa' },
  minMeters: { label: 'min', unit: 'm' },
  radiusMeters: { label: 'radius', unit: 'm' },
  disagreementMeters: { label: 'disagreement', unit: 'm' },
  highRatio: { label: 'high', shape: 'percent' },
  lowRatio: { label: 'low', shape: 'percent' },
  maxCycles: { label: 'max' },
  debounceMs: { label: 'waits', shape: 'duration' },
  maxRunMs: { label: 'runs', shape: 'duration', tail: 'non-stop' },
  cycleWindowMs: { label: 'within', shape: 'duration' },
  staleMs: { label: 'silent', shape: 'duration' },
  offlineMs: { label: 'out of touch', shape: 'duration' },
};

/**
 * What each recipe threshold param MEASURES (ADR 0189) — the mapping that
 * decides which numbers may be restated in the account's units. Absent ⇒ never
 * converts, which is the answer for every voltage, count, duration, ratio, and
 * for `minKpa`: engine oil pressure in inches of mercury is a barometric unit
 * on an engine gauge, and no marine display shows one.
 *
 * MIRRORED from `alertTemplates.alarmParamCategory`, which is the same table
 * for the EDIT boxes, and pinned key-for-key by a parity test in
 * `alarmDisplay.test.ts` — the convention this module already uses for the
 * protocol vocabulary at the top of the file.
 *
 * It is mirrored rather than imported because the import cannot exist:
 * `alertTemplates` builds `PARAM_LIVE_DOMAIN` at MODULE LEVEL out of this
 * module's `unitVocabulary`, so importing it back here makes a cycle whose
 * bodies evaluate in the wrong order — every suite that reaches this module
 * first dies on `unitVocabulary is not a function` before a test runs. The
 * parity test is the drift guard the import would have been.
 */
const PARAM_UNIT_CATEGORY: Readonly<Record<string, UnitCategory>> = {
  maxCelsius: 'temperature',
  minMeters: 'depth',
  radiusMeters: 'shortDistance',
  disagreementMeters: 'shortDistance',
};

/** One param's spec with its {@link PARAM_UNIT_CATEGORY} category attached. */
function thresholdSpecFor(key: string): ThresholdSpec | undefined {
  const spec = THRESHOLD_LABELS[key];
  if (spec === undefined) return undefined;
  const category = PARAM_UNIT_CATEGORY[key];
  return category === undefined ? spec : { ...spec, category };
}

// ---------------------------------------------------------------------------
// STORE unit → the vocabulary the owner reads. THE one table.
//
// {@link THRESHOLD_LABELS} above writes a recipe's AUTHORED numbers ("min
// 100 kPa", "max 95 °C"). A live reading and the compiled trigger beside it are
// STORE-domain: the boat reports oil pressure in hPa and temperature under the
// bare glyph `C` (`packages/telemetry` families). Printed raw beside the summary
// they produce two vocabularies on one row — "Now 1013 hPa · alarms below
// 1000 hPa" sitting under "min 100 kPa" — and nothing on screen says they are
// the same quantity.
//
// So every store unit that reads differently in the owner's vocabulary is listed
// HERE, once, and both halves of a row go through it together. Anything not
// listed passes through untouched: an unrecognised unit is not an invitation to
// invent a conversion.
//
// The rescale gate is the part that matters. `C → °C` changes only the glyph, so
// it is always safe. `hPa → kPa` changes the NUMBER, and is only right where the
// row's other text is in the recipe's authored domain. A CUSTOM alarm's set
// point is typed in the signal's own store unit (`compileCustom` converts
// nothing), and a barometer authors in hPa — for those the raw store unit IS the
// owner's vocabulary, and rescaling would misstate the alarm. Callers pass
// `rescale` only when they know the row is a recipe alarm's.
// ---------------------------------------------------------------------------

/** How a STORE-domain number is restated in the vocabulary an owner reads. */
export interface UnitVocabulary {
  /** Multiply a store-domain number by this. `1` for a pure glyph change. */
  readonly factor: number;
  /** The unit to print after it — `null` when the signal declares none. */
  readonly unit: string | null;
}

// `KPA_TO_HPA` is IMPORTED from `@neomaris/rules`, never mirrored: the factor
// that undoes the compiler's conversion has to be the compiler's own, or a
// retune leaves the panel restating a setpoint the rule does not hold.
const UNIT_VOCABULARY: Readonly<Record<string, UnitVocabulary>> = {
  C: { factor: 1, unit: '°C' },
  hPa: { factor: 1 / KPA_TO_HPA, unit: 'kPa' },
};

/**
 * The vocabulary for one signal's declared STORE unit — see the note above.
 *
 * `rescale` opts into the entries that change the number as well as the glyph,
 * and belongs to the caller because only the caller knows whether the rest of
 * the row is in the recipe's authored domain. Without it a rescaling entry
 * degrades to its store unit rather than mis-scaling, which is the same
 * withhold-rather-than-guess rule {@link setpointPhrase} follows.
 */
export function unitVocabulary(
  storeUnit: string | null | undefined,
  opts: { readonly rescale?: boolean } = {}
): UnitVocabulary {
  const key = (storeUnit ?? '').trim();
  const passthrough: UnitVocabulary = { factor: 1, unit: key.length > 0 ? key : null };
  if (key.length === 0) return passthrough;
  const mapped = UNIT_VOCABULARY[key];
  if (mapped === undefined) return passthrough;
  return mapped.factor === 1 || opts.rescale === true ? mapped : passthrough;
}

function humanizeKey(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return spaced.length > 0 ? spaced : key;
}

function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100);
}

/**
 * One threshold as a fragment: `low 11.8 V`, `waits 5 s`, `high 90 %`, `runs
 * 45 s non-stop`, and — for an account that asked for feet — `min 9.8 ft`.
 *
 * Durations go through the SHARED {@link formatDuration} — the same one the
 * custom-alarm sentence uses — so a 90-second window reads `1 min 30 s`
 * wherever it appears rather than once as minutes and once as milliseconds.
 * Percents and volts are rounded by `roundedReading` (TOM-860): one
 * decimal below 10 % and below 50 V, none at or above those. That rounding also
 * removes the float noise of `0.1 * 100`, which is `10.000000000000002`.
 *
 * `units` converts a param whose category says what it measures (ADR 0189).
 * The STORED number is untouched — this renders a summary nothing writes back —
 * and a param with no category (oil pressure's `minKpa`, a voltage, a count, a
 * duration, a ratio) is not converted, as is every param for an account whose
 * unit for that category is already the stored one.
 */
function formatThreshold(key: string, value: number, units?: UnitSettings): string {
  // A detector contact's polarity (`@neomaris/rules` `CONTACT_ALARM_STATE_KEY`,
  // TOM-136): a 1/0 channel reading, not a quantity. "alarm state 0" says
  // nothing to an owner; which reading sounds the alarm does.
  if (isContactStateParam(key) && (value === 0 || value === 1)) {
    return value === 1 ? 'alarms when the channel is on' : 'alarms when the channel is off';
  }
  const spec = thresholdSpecFor(key);
  const label = spec?.label ?? humanizeKey(key);
  const unit = spec?.unit ?? '';
  let body: string;
  if (spec?.shape === 'duration') body = formatDuration(value);
  else if (spec?.shape === 'percent')
    body = `${roundedReading(value * 100, '%') ?? formatNumber(value * 100)} %`;
  else {
    const converted = convertedQuantity(value, spec?.storeUnit ?? unit, spec?.category, units);
    body =
      converted !== undefined
        ? `${converted.text} ${converted.unit}`
        : unit.length > 0
          ? `${roundedReading(value, unit) ?? formatNumber(value)} ${unit}`
          : formatNumber(value);
  }
  return spec?.tail === undefined ? `${label} ${body}` : `${label} ${body} ${spec.tail}`;
}

/**
 * Pick the thresholds to summarize: the first enabled mode present in the map,
 * else the first map entry (a definition may key thresholds by a single mode).
 */
function pickThresholds(
  enabledModes: readonly string[],
  thresholdsByMode: Record<string, Record<string, unknown>>
): Record<string, unknown> {
  for (const mode of enabledModes) {
    const t = thresholdsByMode[mode];
    if (t && typeof t === 'object') return t;
  }
  for (const value of Object.values(thresholdsByMode)) {
    if (value && typeof value === 'object') return value;
  }
  return {};
}

/** The stored modes worth printing — strings with something in them. */
function presentModes(enabledModes: readonly string[]): string[] {
  return enabledModes.filter((m) => typeof m === 'string' && m.trim().length > 0);
}

/** The threshold half of a context line: `low 11.8 V, waits 5 s`, or empty. */
function thresholdSummary(
  modes: readonly string[],
  thresholdsByMode: Record<string, Record<string, unknown>>,
  units?: UnitSettings
): string {
  return numericThresholdFields(pickThresholds(modes, thresholdsByMode))
    .map(({ key, value }) => formatThreshold(key, value, units))
    .join(', ');
}

/** Join the two halves of a row's context line, dropping an empty one. */
function joinContext(left: string, right: string): string {
  if (left.length > 0 && right.length > 0) return `${left} · ${right}`;
  return left.length > 0 ? left : right;
}

/**
 * A one-line per-mode threshold summary: the enabled modes joined, then ` · `,
 * then each numeric threshold as `label value unit` — e.g.
 * `Anchored, Underway · low 11.8 V`. Modes and thresholds RENDER FROM THE PAYLOAD
 * (custom modes appear verbatim); non-numeric thresholds are omitted.
 *
 * The READ surfaces use {@link formatWatchSummary} instead — same thresholds,
 * with the modes stated in the preset vocabulary the authoring form now speaks.
 * This one stays for the places that want the raw stored set.
 *
 * `units` is the account's (`useUnits()`): the numbers read in the account's
 * units, the stored payload is untouched (ADR 0189). Omit it and every
 * threshold prints stored, exactly as before.
 */
export function formatModeSummary(
  enabledModes: readonly string[],
  thresholdsByMode: Record<string, Record<string, unknown>>,
  units?: UnitSettings
): string {
  const modes = presentModes(enabledModes);
  return joinContext(modes.join(', '), thresholdSummary(modes, thresholdsByMode, units));
}

// ---------------------------------------------------------------------------
// The watch schedule, in the preset vocabulary (Phase 1 slice 4).
//
// The form asks "when should this alarm watch?" as three sentences — Always /
// Only underway / Only at rest — and then the list row answered with the raw
// mode set it wrote ("Anchored, Docked, Moored"). Same question, two
// vocabularies, and the reader has to fold six modes back into the sentence
// they picked. The row now speaks the form's words, through the SAME detector
// the form's radio uses (`watchSchedule`), so the two can never disagree.
// ---------------------------------------------------------------------------

/**
 * The modes half of a row's context line in the preset vocabulary: `Always`,
 * `Only underway`, `Only at rest` — the {@link WATCH_PRESETS} labels verbatim —
 * and the stored TitleCase mode list for a set that matches no preset.
 *
 * Detection is the tolerant one from `watchSchedule` (case-folded, `Sailing`
 * folded into `Underway`), so an alarm authored before Sailing was reachable
 * still reads as the sentence it means. It never rewrites anything: the label
 * is a reading of `enabledModes`, and the authoring form is where the gap that
 * tolerance glosses over is disclosed (`watchGapNote`).
 */
export function watchVocabulary(enabledModes: readonly string[]): string {
  const modes = presentModes(enabledModes);
  const preset = detectWatchPreset(modes);
  return WATCH_PRESETS.find((p) => p.id === preset)?.label ?? modes.join(', ');
}

/**
 * The schedule on its own, as a sentence: `Watches: Always`, and `''` for a
 * definition with no modes at all.
 *
 * Lives here rather than in the two callers because the LEAD-IN is part of the
 * vocabulary: the detail page states the schedule without the thresholds (it
 * prints those in a full per-mode table underneath), and the list row states
 * both. Both must say "Watches: …" identically — the divergence this fixes was
 * the detail page rendering the raw mode set while the row beside it said
 * "Watches: Always".
 */
export function watchScheduleSentence(enabledModes: readonly string[]): string {
  const watch = watchVocabulary(enabledModes);
  return watch.length > 0 ? `Watches: ${watch}` : '';
}

/**
 * A row's context line with the modes stated as the watch schedule —
 * `Watches: Always · low 11.8 V`. The threshold half is
 * {@link formatModeSummary}'s, unchanged.
 */
export function formatWatchSummary(
  enabledModes: readonly string[],
  thresholdsByMode: Record<string, Record<string, unknown>>,
  units?: UnitSettings
): string {
  const modes = presentModes(enabledModes);
  return joinContext(
    watchScheduleSentence(modes),
    thresholdSummary(modes, thresholdsByMode, units)
  );
}

/**
 * A mode id as the owner reads it. Canonical when this build recognises it
 * (case-folded through the shared vocabulary, so a legacy `anchored` reads
 * `Anchored`), otherwise the stored id with its first letter capitalized — a
 * mode a newer boat elects is named, never swallowed.
 *
 * `Sailing` stays `Sailing`. The Sailing→Underway fold belongs to schedule
 * COMPARISON (`canonicalWatchModes`); telling an owner their boat is
 * "Underway" while it is sailing would be restating the boat's own report.
 */
export function formatModeLabel(modeId: string): string {
  const canonical = foldModesToCanonical([modeId])[0];
  if (canonical !== undefined) return canonical;
  const trimmed = modeId.trim();
  return trimmed.length === 0 ? '' : trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

/** One mode's numeric thresholds, each already formatted `label value unit`. */
export interface ModeThresholdRow {
  readonly mode: string;
  readonly thresholds: { key: string; text: string }[];
}

/**
 * The FULL per-mode threshold breakdown for a definition — every enabled mode
 * (in declared order), then any extra keys present in `thresholdsByMode` that
 * weren't in `enabledModes`, each with its numeric thresholds rendered via the
 * shared recipe labels. Unlike {@link formatModeSummary} (a one-line summary of
 * the first mode) this keeps EVERY mode, so the detail page shows a real table —
 * fixing the UI-1 low where a multi-mode alarm only ever showed its first mode.
 * Pure; a mode with no numeric thresholds yields an empty `thresholds` array.
 */
export function perModeThresholds(
  enabledModes: readonly string[],
  thresholdsByMode: Record<string, Record<string, unknown>>,
  units?: UnitSettings
): ModeThresholdRow[] {
  const modes: string[] = [];
  for (const m of enabledModes) {
    if (typeof m === 'string' && m.trim().length > 0 && !modes.includes(m)) modes.push(m);
  }
  for (const m of Object.keys(thresholdsByMode)) {
    if (!modes.includes(m)) modes.push(m);
  }
  return modes.map((mode) => {
    const map = thresholdsByMode[mode];
    const thresholds =
      map && typeof map === 'object'
        ? numericThresholdFields(map).map(({ key, value }) => ({
            key,
            text: formatThreshold(key, value, units),
          }))
        : [];
    return { mode, thresholds };
  });
}

// ---------------------------------------------------------------------------
// Custom (non-recipe) alarm rendering.
//
// A custom alarm has no recipe, so the recipe vocabulary above renders nothing
// useful for it: its numbers ride `thresholdsByMode` under the RESERVED keys
// (`setpoint`/`clearpoint`/`setDelayMs`/`clearDelayMs`/`staleMs`), which the
// threshold table would print as `setpoint 26, set delay ms 5000` — jargon that
// says nothing about what the alarm watches. These helpers turn the stored spec
// into plain English instead: one sentence for the list, and the
// signal / fires / clears / delays rows for the detail page.
//
// Two honesty rules, both mirrored from the compiler (`compileCustom` in
// `@neomaris/rules`) rather than from the form that authored the alarm:
//   - an absent clear point means NO hysteresis (the compiler defaults it to
//     the set point), so "Clears when" names the set point rather than blanking;
//   - a `DataUnavailable` alarm takes its window from `staleMs` (default 5 min)
//     and the compiler IGNORES its set/clear delays, so no delay row is shown.
// ---------------------------------------------------------------------------

/**
 * The synthetic recipe-id prefix a custom alarm compiles under
 * (`custom:<defId>`). MIRRORED from `@neomaris/rules` `CUSTOM_RECIPE_PREFIX` —
 * the web build has no runtime dependency on that package, the same mirror
 * convention as the protocol vocabulary at the top of this file.
 */
export const CUSTOM_RECIPE_PREFIX = 'custom:';

/** The staleness window a `DataUnavailable` custom alarm gets when none is
 *  authored — mirrors the compiler's `DEFAULT_CUSTOM_STALE_MS` (5 min). */
const DEFAULT_CUSTOM_STALE_MS = 300_000;

/** The `definition.custom` block as it arrives on the wire. */
export interface AlarmCustomSpec {
  /** The one concrete telemetry path this alarm is pinned to. */
  readonly path: string;
  /**
   * The trigger class. Deliberately a plain `string`, not the v1 union: a
   * definition written by a NEWER build can carry a class this build has never
   * heard of, and every renderer here degrades to a neutral phrase instead of
   * inventing a threshold.
   */
  readonly triggerClass: string;
}

/**
 * Read `definition.custom` defensively — `undefined` unless the row really
 * carries a usable `{ path, triggerClass }`. Everything custom-shaped goes
 * through here, so a null / partial / corrupt block degrades to "not custom"
 * (the caller then falls back to the recipe rendering, or to the honest
 * couldn't-read note when the recipe id says custom).
 */
export function readCustomSpec(def: { readonly custom?: unknown }): AlarmCustomSpec | undefined {
  const raw = def.custom;
  if (raw === null || typeof raw !== 'object') return undefined;
  const { path, triggerClass } = raw as { path?: unknown; triggerClass?: unknown };
  if (typeof path !== 'string' || path.trim().length === 0) return undefined;
  if (typeof triggerClass !== 'string' || triggerClass.trim().length === 0) return undefined;
  return { path: path.trim(), triggerClass: triggerClass.trim() };
}

/**
 * Whether a definition is a CUSTOM (non-recipe) alarm. True on either signal:
 * a readable `custom` block, or a `custom:<defId>` recipe id. The second arm
 * matters — a def whose block is missing or corrupt is still not a recipe, so
 * the surfaces must not offer it a recipe editor.
 */
export function isCustomDefinition(def: {
  readonly recipeId?: string;
  readonly custom?: unknown;
}): boolean {
  return readCustomSpec(def) !== undefined || (def.recipeId ?? '').startsWith(CUSTOM_RECIPE_PREFIX);
}

/** Everything the list and detail surfaces render for a custom alarm. */
export interface CustomAlarmSetup {
  /** The pinned telemetry path, verbatim (rendered mono, never as the name). */
  readonly path: string;
  /** Human name for the signal, derived from the path alone. */
  readonly signalName: string;
  /** The stored trigger class, verbatim. */
  readonly triggerClass: string;
  /** The whole alarm as one plain-English line (the list row / review sentence). */
  readonly sentence: string;
  /** "Fires when" fragment, e.g. `Goes above 26 V`. */
  readonly firesWhen: string;
  /** "Clears when" fragment, e.g. `Drops back below 25.5 V`. */
  readonly clearsWhen: string;
  /**
   * "Delays" row, e.g. `Waits 5 s before it fires`. `undefined` when the class
   * has no delay semantics (`DataUnavailable`, or a class from a newer build).
   */
  readonly delays?: string;
}

function finiteThreshold(thresholds: Record<string, unknown>, key: string): number | undefined {
  const raw = thresholds[key];
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : undefined;
}

function firesWhenFragment(
  triggerClass: string,
  thresholds: Record<string, unknown>,
  unit: string | undefined,
  category?: UnitCategory,
  units?: UnitSettings
): string {
  switch (triggerClass) {
    case 'High':
    case 'Low': {
      const setpoint = finiteThreshold(thresholds, 'setpoint');
      const verb = triggerClass === 'High' ? 'Goes above' : 'Drops below';
      return setpoint === undefined
        ? `${verb} its set point`
        : `${verb} ${formatValueWithUnit(setpoint, unit, category, units)}`;
    }
    case 'On':
      return 'Turns on';
    case 'Off':
      return 'Turns off';
    case 'DataUnavailable': {
      const staleMs = finiteThreshold(thresholds, 'staleMs') ?? DEFAULT_CUSTOM_STALE_MS;
      return `Stops reporting for ${formatDuration(staleMs)}`;
    }
    default:
      return 'Meets its trigger';
  }
}

function clearsWhenFragment(
  triggerClass: string,
  thresholds: Record<string, unknown>,
  unit: string | undefined,
  category?: UnitCategory,
  units?: UnitSettings
): string {
  switch (triggerClass) {
    case 'High':
    case 'Low': {
      const setpoint = finiteThreshold(thresholds, 'setpoint');
      // The compiler defaults an absent clear point to the set point (no
      // hysteresis), so that is what the boat will actually run.
      const clearpoint = finiteThreshold(thresholds, 'clearpoint') ?? setpoint;
      const verb = triggerClass === 'High' ? 'Drops back below' : 'Goes back above';
      return clearpoint === undefined
        ? `${verb} its set point`
        : `${verb} ${formatValueWithUnit(clearpoint, unit, category, units)}`;
    }
    case 'On':
      return 'Turns back off';
    case 'Off':
      return 'Turns back on';
    case 'DataUnavailable':
      return 'It reports again';
    default:
      return 'The trigger stops being met';
  }
}

function delaysSummary(
  triggerClass: string,
  thresholds: Record<string, unknown>
): string | undefined {
  // DataUnavailable's window rides `staleMs` and is already in "Fires when";
  // the compiler ignores its set/clear delays, so a delay row would be a lie.
  // An unrecognized class gets no row for the same reason.
  if (!['High', 'Low', 'On', 'Off'].includes(triggerClass)) return undefined;
  const setDelayMs = finiteThreshold(thresholds, 'setDelayMs');
  const clearDelayMs = finiteThreshold(thresholds, 'clearDelayMs');
  const parts: string[] = [];
  if (setDelayMs !== undefined && setDelayMs > 0) {
    parts.push(`${formatDuration(setDelayMs)} before it fires`);
  }
  if (clearDelayMs !== undefined && clearDelayMs > 0) {
    parts.push(`${formatDuration(clearDelayMs)} before it clears`);
  }
  return parts.length > 0 ? `Waits ${parts.join(' and ')}` : 'None — it fires and clears at once';
}

/**
 * Derive everything the surfaces render for a CUSTOM alarm, or `null` when the
 * definition is not custom (or its block is unreadable).
 *
 * The signal name uses the caller's current catalog label, or `signalLabel`
 * when no catalog is available. `opts.unit` is the
 * signal's declared store unit when the caller already has it (the detail page
 * reads it from the live-sample feed it is already subscribed to); without it
 * the numbers print bare rather than in a guessed unit.
 *
 * A custom alarm has NO recipe param key, so its category comes from the path
 * it is pinned to (`categoryFor`) — the same resolution the live reading beside
 * it uses. Pass `opts.units` and the trigger numbers read in the account's
 * units; omit it and they print stored, as they always have. The stored
 * thresholds are untouched either way (ADR 0189). Condition clauses stay in
 * store units on every surface — see `alarmSentence`'s `ConditionsOptions`.
 */
export function customAlarmSetup(
  def: {
    readonly custom?: unknown;
    readonly conditions?: unknown;
    readonly enabledModes: readonly string[];
    readonly thresholdsByMode: Record<string, Record<string, unknown>>;
  },
  opts: {
    /** The boat's current name for the trigger or condition path. */
    readonly labelFor?: (path: string) => string;
    readonly unit?: string | null;
    /** The store unit for a CONDITION clause's path, when the caller has it. */
    readonly unitFor?: (path: string) => string | null | undefined;
    /** The account's units (`useUnits()`), for the trigger numbers. */
    readonly units?: UnitSettings;
    /** The sentence with stored numbers, as titles spell them (TOM-860). */
    readonly exact?: boolean;
    /**
     * The sentence without its "only while …" tail. The builder prefills a
     * title that stops at the trigger (TOM-860).
     */
    readonly withoutConditions?: boolean;
  } = {}
): CustomAlarmSetup | null {
  const custom = readCustomSpec(def);
  if (custom === undefined) return null;
  const thresholds = pickThresholds(def.enabledModes, def.thresholdsByMode);
  const signalName = opts.labelFor?.(custom.path) ?? signalLabel(custom.path);
  const unit = opts.unit ?? undefined;
  const category = categoryFor(custom.path, unit);
  const units = opts.units;
  // The class is a wire string; the sentence's own default arm covers a value
  // outside the v1 union, so the cast never widens what actually renders.
  const spec: CustomSentenceSpec = {
    triggerClass: custom.triggerClass as TriggerClass,
    path: custom.path,
  };
  // The condition tail uses the same current names as the trigger.
  const conditionClause =
    opts.withoutConditions === true
      ? ''
      : conditionsClause(readConditions(def.conditions), {
          ...(opts.labelFor !== undefined ? { labelFor: opts.labelFor } : {}),
          ...(opts.unitFor !== undefined ? { unitFor: opts.unitFor } : {}),
          ...(opts.exact === true ? { exact: true } : {}),
        });
  return {
    path: custom.path,
    signalName,
    triggerClass: custom.triggerClass,
    sentence: customAlarmSentence(spec, thresholds, {
      label: signalName,
      unit,
      conditionClause,
      ...(category === undefined ? {} : { category }),
      ...(units === undefined ? {} : { units }),
      ...(opts.exact === true ? { exact: true } : {}),
    }),
    firesWhen: firesWhenFragment(custom.triggerClass, thresholds, unit, category, units),
    clearsWhen: clearsWhenFragment(custom.triggerClass, thresholds, unit, category, units),
    delays: delaysSummary(custom.triggerClass, thresholds),
  };
}

// ---------------------------------------------------------------------------
// Conditions ("Only when …") — recipe AND custom alarms.
//
// A definition can carry a qualifier condition list: 1..4 clauses folded with
// `all` (AND) or `any` (OR), AND-ed with the trigger. Two things must be said
// honestly wherever they are rendered:
//
//   - FAIL-CLOSED. On the boat a clause with no live data reads FALSE, never
//     true. Under `all` that means a broken sensor silences the alarm. That is
//     a safety property an operator has to know about, so the note is permanent,
//     not a hover tip.
//   - CONDITION UNBOUND (plan D2). When a recipe's engine-running preset can't
//     be resolved — the boat reports no engine RPM — the compiler ARMS THE
//     ALARM WITHOUT IT rather than disarming a safety alarm behind a broken
//     tachometer. The stored definition still carries the condition, so the
//     screen would otherwise claim a condition that is not in force.
// ---------------------------------------------------------------------------

/** One condition clause, rendered for a read surface. */
export interface AlarmConditionRow {
  /** The clause in plain English, e.g. `The engine is running (above 300 RPM)`. */
  readonly text: string;
  /** The clause's telemetry path, when it has one (rendered mono under the text). */
  readonly path?: string;
  /**
   * True for the engine preset the compiler DROPPED (plan D2): authored, shown,
   * but NOT in force. Scoped to the row rather than the block, because the
   * other clauses beside it are still gating the alarm.
   */
  readonly unbound?: boolean;
}

/** Everything a read surface renders for a definition's conditions. */
export interface AlarmConditionsSetup {
  /** How the clauses fold: `all` (AND, the default) or `any` (OR). */
  readonly logic: 'all' | 'any';
  /** The fold in words, or empty when there is fewer than two clauses in force. */
  readonly logicText: string;
  /** One row per readable clause, in stored order. Never empty. */
  readonly rows: AlarmConditionRow[];
  /**
   * The rows STILL GATING the alarm — every row bar a dropped preset. Empty
   * when the dropped preset was the only clause: then nothing is in force.
   */
  readonly enforcedRows: AlarmConditionRow[];
  /**
   * The permanent fail-closed disclosure for the clauses in force, worded for
   * this fold. `undefined` when no clause is in force — there is nothing
   * fail-closed about an alarm nothing is gating.
   */
  readonly failClosedNote?: string;
  /** True when the compiler dropped the engine preset (see {@link CONDITION_UNBOUND_NOTE}). */
  readonly unbound: boolean;
}

/**
 * The honest note for a definition whose engine-running preset was DROPPED at
 * compile time because the boat reports no engine RPM (plan D2). The alarm is
 * armed — it just isn't gated by the engine.
 */
export const CONDITION_UNBOUND_NOTE =
  'The engine-running condition couldn’t be attached — the boat isn’t reporting engine RPM — so this alarm is armed without it and fires regardless of engine state.';

/** The short chip for the same state, for a list row. */
export const CONDITION_UNBOUND_CHIP = 'Engine-running condition not attached';

/**
 * The fold, in words. Empty for a single clause — there is nothing to fold.
 * The `any` wording is the condition builder's ("Any one of these is enough",
 * `ConditionBuilder.tsx`): "can be true" reads as mere possibility, where the
 * point is sufficiency.
 */
function conditionLogicText(logic: 'all' | 'any', clauseCount: number): string {
  if (clauseCount < 2) return '';
  return logic === 'any' ? 'Any one of these is enough.' : 'All of these must be true.';
}

/**
 * The permanent fail-closed disclosure, worded for the fold. Mirrors the
 * evaluator: a clause with no live value reads FALSE (never true), and the
 * clauses then fold with `all` / `any`.
 *
 * The `any` wording is the builder's (`customAlarmConditions.failClosedNote`)
 * and is deliberately weaker: under `any` a silent signal removes ONE way for
 * the alarm to fire, so claiming a broken sensor silences the alarm outright
 * would be false. One clause folds the same either way, so a lone `any` clause
 * takes the `all` wording.
 */
export function conditionFailClosedNote(logic: 'all' | 'any', clauseCount = 2): string {
  return logic === 'any' && clauseCount > 1
    ? 'A condition needs live data to read true. If one of these signals stops reporting, that condition reads false — so this alarm fires only if one of the others is true. A broken sensor can silence it.'
    : 'Every condition here has to read true for this alarm to fire. If the boat stops reporting one of these signals that condition reads false, so a broken sensor silences this alarm too.';
}

/** `The engine is running (above 300 RPM)` / `Battery 1 voltage is above 14 V`. */
function conditionRow(
  clause: ConditionClauseSpec,
  unitFor?: (path: string) => string | null | undefined,
  labelFor?: (path: string) => string
): AlarmConditionRow | undefined {
  if ('preset' in clause) {
    const minRpm = clause.minRpm;
    return {
      text:
        minRpm !== undefined && Number.isFinite(minRpm)
          ? `The engine is running (above ${formatNumber(minRpm)} RPM)`
          : 'The engine is running',
    };
  }
  const path = clause.path;
  if (typeof path !== 'string' || path.trim().length === 0) return undefined;
  const name = labelFor?.(path) ?? signalLabel(path);
  const unit = unitFor?.(path);
  switch (clause.type) {
    case 'High':
    case 'Low': {
      const value = clause.value;
      if (value === undefined || !Number.isFinite(value)) return undefined;
      const direction = clause.type === 'High' ? 'above' : 'below';
      return { text: `${name} is ${direction} ${formatValueWithUnit(value, unit)}`, path };
    }
    case 'Active':
      return { text: `${name} is on`, path };
    case 'Inactive':
      return { text: `${name} is off`, path };
    default:
      return undefined;
  }
}

/**
 * Everything the read surfaces render for a definition's conditions, or `null`
 * when it carries none this build can read. Works for a recipe alarm (the
 * engine-running preset) and a custom one (explicit path clauses) alike.
 *
 * A DROPPED preset (D2) is marked on its own row, not on the block: a
 * definition can carry the dropped preset AND explicit clauses that still gate
 * the alarm fail-closed, and hiding those behind the preset's banner would
 * suppress the disclosure that matters most. `logicText` and `failClosedNote`
 * therefore describe the rows still in force, not everything stored.
 */
export function alarmConditionsSetup(
  def: { readonly conditions?: unknown; readonly conditionUnbound?: unknown },
  opts: {
    readonly unitFor?: (path: string) => string | null | undefined;
    readonly labelFor?: (path: string) => string;
  } = {}
): AlarmConditionsSetup | null {
  const conditions = readConditions(def.conditions);
  if (conditions === undefined) return null;
  const unbound = readConditionUnbound(def);
  const logic = conditions.logic === 'any' ? 'any' : 'all';
  // Mirror the compiler exactly: under an ANY fold that lost its preset leg the
  // compiler omits the WHOLE condition (an any-of missing one leg cannot be
  // evaluated honestly), so NO clause is in force — claiming the survivors
  // still gate the alarm would be the lie this surface exists to prevent.
  const wholeBlockDropped = unbound && logic === 'any';
  const rows: AlarmConditionRow[] = [];
  for (const clause of conditions.clauses) {
    const row = conditionRow(clause, opts.unitFor, opts.labelFor);
    if (row === undefined) continue;
    rows.push(
      (unbound && 'preset' in clause) || wholeBlockDropped ? { ...row, unbound: true } : row
    );
  }
  if (rows.length === 0) return null;
  const enforcedRows = rows.filter((r) => r.unbound !== true);
  return {
    logic,
    logicText: conditionLogicText(logic, enforcedRows.length),
    rows,
    enforcedRows,
    ...(enforcedRows.length > 0
      ? { failClosedNote: conditionFailClosedNote(logic, enforcedRows.length) }
      : {}),
    unbound,
  };
}

/** Whether the boat's compiler dropped this alarm's engine-running preset (D2). */
export function readConditionUnbound(def: { readonly conditionUnbound?: unknown }): boolean {
  return def.conditionUnbound === true;
}

// ---------------------------------------------------------------------------
// The instance PIN — which engine, bank or tank this alarm watches (ADR-097 §4)
// ---------------------------------------------------------------------------

/**
 * The stored pin's path, or `undefined` when the definition has none.
 *
 * Defensive by design (see {@link AlarmDefView.binding}): `null`, a missing
 * block, a non-object, or a blank/non-string `path` all read as "no pin" —
 * which is the safe reading, because it means the alarm keeps the family
 * first-match it has always had rather than a surface inventing a pin.
 */
export function readBindingPath(def: { readonly binding?: unknown }): string | undefined {
  const raw = def.binding;
  if (raw === null || typeof raw !== 'object') return undefined;
  const { path } = raw as { path?: unknown };
  if (typeof path !== 'string') return undefined;
  const trimmed = path.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/** What a pinned alarm watches, for the detail page's "Watching" row. */
export interface AlarmBindingSetup {
  /** The pinned telemetry path, printed mono beside the name. */
  readonly path: string;
  /** The instance in human words — "Port engine oil pressure". */
  readonly label: string;
  /**
   * Chip copy, present when the boat is NOT actually watching that instance: the
   * pin is hard, so the compiler reports the definition pending instead of
   * taking a live sibling. Read from the pending reason the boat already sends
   * (it names the path) — no new signal, and no guess when the def is pending
   * for some other reason.
   */
  readonly note?: string;
}

/**
 * The "Watching" row for one definition, or `undefined` when it has no pin.
 *
 * A pin is invisible everywhere except the editor without this: two engines
 * report, the alarm watches ONE of them, and every read-only surface says only
 * "engine oil pressure" — which reads as "both". Naming the instance is the
 * whole point of the pin.
 */
export function alarmBindingSetup(
  def: {
    readonly binding?: unknown;
    readonly compileStatus?: string | null;
    readonly compileReason?: string | null;
  },
  options: { readonly labelFor?: (path: string) => string } = {}
): AlarmBindingSetup | undefined {
  const path = readBindingPath(def);
  if (path === undefined) return undefined;
  const labelFor = options.labelFor ?? ((p: string) => signalLabel(p));
  const reason = def.compileStatus === 'pending' ? (def.compileReason ?? '') : '';
  // Only a pending reason that NAMES this path says anything about the pin; a
  // def pending for an unrelated reason must not be reported as an unbound pin.
  const aboutThePin = reason.includes(path);
  const note = !aboutThePin
    ? undefined
    : // The compiler's two pin reasons call for different fixes: repair/await the
      // instrument, versus re-pin the alarm somewhere it can bind at all.
      reason.includes('does not fit')
      ? 'Does not fit this alarm'
      : 'Not currently reported';
  return { path, label: labelFor(path), ...(note !== undefined ? { note } : {}) };
}

// ---------------------------------------------------------------------------
// The LIVE VALUE block (ADR 0170) — what the alarm watches, and what it watches
// for, as the compiler resolved it.
//
// This is what lets a row read "Now 12.6 V · alarms below 11.8 V" instead of the
// set point alone. Two honesty rules, both from the ADR:
//
//   - A PENDING definition has no value line at all. It is not armed, so a
//     present-tense reading beside it would say the boat is watching something
//     it is not.
//   - `resolvedPath` is COMPILE TRUTH and wins whenever the field is on the
//     wire. `custom.path` / `binding.path` are AUTHORING INTENT and are consulted
//     only when the field is ABSENT — i.e. an older cloud. (An unpinned recipe
//     alarm has no `binding.path` at all, and a pin the compiler rejected points
//     at a signal nothing is watching.)
//
// And the prohibition: NEVER walk the recipe's path family here to guess an
// instance. That is a second implementation of the compiler's binding rules,
// which would drift silently and then name the wrong tank. See ADR 0170.
// ---------------------------------------------------------------------------

/**
 * The compiled trigger, read defensively. `null` for a definition with no
 * compiled rule, for an older cloud that sends no such field, and for a block
 * whose `class` is missing or blank — a trigger with no class says nothing.
 * Non-finite setpoints are dropped rather than echoed.
 */
export function readWireTrigger(def: { readonly trigger?: unknown }): AlarmWireTrigger | null {
  const raw = def.trigger;
  if (raw === null || typeof raw !== 'object') return null;
  const { class: cls, setpoint, clearpoint } = raw as Record<string, unknown>;
  if (typeof cls !== 'string' || cls.trim().length === 0) return null;
  return {
    class: cls.trim(),
    ...(typeof setpoint === 'number' && Number.isFinite(setpoint) ? { setpoint } : {}),
    ...(typeof clearpoint === 'number' && Number.isFinite(clearpoint) ? { clearpoint } : {}),
  };
}

/**
 * The compiled rule's resolved condition paths, read defensively — `[]` for an
 * older cloud, a malformed block, or a rule with no condition. Non-string and
 * blank entries are dropped rather than rendered.
 */
export function wireConditionPaths(def: { readonly conditionPaths?: unknown }): string[] {
  const raw = def.conditionPaths;
  if (!Array.isArray(raw)) return [];
  const paths: string[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'string') continue;
    const trimmed = entry.trim();
    if (trimmed.length > 0 && !paths.includes(trimmed)) paths.push(trimmed);
  }
  return paths;
}

/**
 * The path whose live reading may be shown beside this alarm's set point, or
 * `null` when there is none to show. The ordering is ADR 0170's, and the order
 * is the point — see the section note above.
 *
 * A PENDING definition is `null` before anything else is consulted: the wire
 * already nulls its `resolvedPath`, but an older cloud sends no field at all,
 * and falling back to its authored pin would print a confident reading beside an
 * alarm that is not armed.
 */
export function resolvedPathFor(def: {
  readonly compileStatus?: string | null;
  readonly resolvedPath?: string | null;
  readonly custom?: unknown;
  readonly binding?: unknown;
}): string | null {
  if (def.compileStatus === 'pending') return null;
  // PRESENT (even as null) ⇒ this cloud knows the answer, so it is the answer.
  if (def.resolvedPath !== undefined) {
    if (typeof def.resolvedPath !== 'string') return null;
    const trimmed = def.resolvedPath.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  // ABSENT ⇒ an older cloud. A custom alarm names its own path; a pinned recipe
  // alarm names its instance. An unpinned recipe alarm has neither, and guessing
  // is forbidden.
  return readCustomSpec(def)?.path ?? readBindingPath(def) ?? null;
}

/**
 * What the alarm watches FOR, in one fragment: `alarms below 11.8 V`, `alarms
 * when on`, `alarms if it stops reporting`.
 *
 * `null` — no phrase at all — for a class this build does not recognise, and for
 * a threshold class whose set point did not survive the wire. Never a guess: the
 * row then shows the reading alone, which is honest, where "alarms above —"
 * would not be.
 *
 * `unit` is the signal's STORE unit (the catalog's declared unit, or the live
 * sample's), because {@link AlarmWireTrigger} numbers are store-domain. Passing
 * a recipe's AUTHORING unit here would label an hPa set point "kPa".
 *
 * `category` + `units` restate the number in the account's units (ADR 0189).
 * Resolve the category from the RESOLVED PATH (`categoryFor(path, unit)`), the
 * same way the reading beside it is resolved, or the row ends up with a set
 * point in one unit and a live value in another.
 */
export function setpointPhrase(
  trigger: AlarmWireTrigger | null | undefined,
  unit?: string | null,
  category?: UnitCategory,
  units?: UnitSettings,
  /** At least this many decimals: `distinctDecimals` for the set point (TOM-860). */
  minDecimals?: number
): string | null {
  if (trigger === null || trigger === undefined) return null;
  const raw = trigger.setpoint;
  const setpoint = typeof raw === 'number' && Number.isFinite(raw) ? raw : undefined;
  switch (trigger.class) {
    case 'High':
      return setpoint === undefined
        ? null
        : `alarms above ${formatValueWithUnit(setpoint, unit, category, units, { minDecimals })}`;
    case 'Low':
      return setpoint === undefined
        ? null
        : `alarms below ${formatValueWithUnit(setpoint, unit, category, units, { minDecimals })}`;
    case 'On':
    case 'EntryOn':
      return 'alarms when on';
    case 'Off':
      return 'alarms when off';
    case 'DataUnavailable':
      return 'alarms if it stops reporting';
    case 'AnchorWatch':
      // The radius is always metres, whatever unit the distance path declares.
      // The compiler carries it as `trigger.triggerRadius`, which the §5.7
      // projection LIFTS into `setpoint` (`compiled-ruleset.triggerOf`); an older
      // cloud that does not says nothing rather than "alarms outside —".
      //
      // Its category is fixed for the same reason the unit is: a watch radius is
      // a short distance, whatever the path under it reads. That is also the
      // category `radiusMeters` carries in the threshold table, so the two
      // statements of one number agree — "radius 131 ft" and "alarms outside
      // 131 ft".
      return setpoint === undefined
        ? null
        : `alarms outside ${formatValueWithUnit(setpoint, 'm', 'shortDistance', units)}`;
    default:
      return null;
  }
}

/**
 * The compiled trigger restated in `vocab` — the counterpart of restating the
 * READING in it, so a row can never label its two halves differently.
 *
 * Only `High` / `Low` are touched, because only their numbers are in the input
 * path's unit. `AnchorWatch`'s lifted radius is in metres whatever the distance
 * path declares (see {@link setpointPhrase}), and the remaining classes carry no
 * number at all — scaling either by the path's vocabulary would invent a
 * quantity.
 */
export function triggerInVocabulary(
  trigger: AlarmWireTrigger | null,
  vocab: UnitVocabulary
): AlarmWireTrigger | null {
  if (trigger === null || vocab.factor === 1) return trigger;
  if (trigger.class !== 'High' && trigger.class !== 'Low') return trigger;
  return {
    class: trigger.class,
    ...(trigger.setpoint === undefined ? {} : { setpoint: trigger.setpoint * vocab.factor }),
    ...(trigger.clearpoint === undefined ? {} : { clearpoint: trigger.clearpoint * vocab.factor }),
  };
}

/**
 * The one-line conditions summary for a list row — `Only while the engine is
 * running (above 300 RPM)`. A preset the compiler DROPPED is left OUT of the
 * line (printing "only while…" for a condition that isn't in force is the lie
 * this whole surface exists to prevent — the row shows
 * {@link CONDITION_UNBOUND_CHIP} instead), but every OTHER clause is still
 * summarised: those are in force, and staying silent about them would be the
 * same lie the other way round.
 *
 * `undefined` when nothing is left to say: no readable conditions, or the
 * dropped preset was the only clause.
 */
export function conditionSummaryLine(
  def: {
    readonly conditions?: unknown;
    readonly conditionUnbound?: unknown;
  },
  options: { labelFor?: (path: string) => string } = {}
): string | undefined {
  const conditions = readConditions(def.conditions);
  if (conditions === undefined) return undefined;
  const unbound = readConditionUnbound(def);
  // Under an ANY fold the compiler drops the WHOLE condition with the preset
  // (see alarmConditionsSetup) — nothing is in force, so say nothing.
  if (unbound && conditions.logic === 'any') return undefined;
  const inForce = unbound ? conditions.clauses.filter((c) => !('preset' in c)) : conditions.clauses;
  if (inForce.length === 0) return undefined;
  const clause = conditionsClause({ ...conditions, clauses: inForce }, options);
  if (clause.length === 0) return undefined;
  return clause.charAt(0).toUpperCase() + clause.slice(1);
}

// ---------------------------------------------------------------------------
// Active-band + configured-band row views (page composition, testable).
// ---------------------------------------------------------------------------

/** A fully-derived active-alarm row (Band 1). Extends the sort shape. */
export interface ActiveAlarmView extends DisplayAlertRow {
  readonly id: string;
  readonly title: string;
  readonly severity?: AlarmSeverity;
  readonly state: AlertState;
  readonly stateMeta: AlertStateMeta;
  readonly lastTransitionAt: string;
  readonly whatHappened?: string;
  readonly occurrence?: string;
  readonly ackLine?: string;
  readonly silenceUntil?: string;
  readonly reactivationAt?: string;
}

/**
 * Derive the active-band view for one row, or `null` when the row is not
 * surfaced (no runtime yet, or a quiescent `Normal`/`Disabled` state).
 *
 * `opts` round a prefilled title in the spelling the configured band uses
 * ({@link displayedTitle}); without them it is matched in store units.
 */
export function toActiveAlarmView(
  row: AlarmAlertRow,
  opts: ConfiguredViewOptions = {}
): ActiveAlarmView | null {
  const runtime = row.runtime;
  if (runtime === null) return null;
  const state = asAlertState(runtime.state);
  if (!isSurfacedState(state)) return null;
  const snap = runtimeSnapshot(runtime);
  const severity = row.definition.severity ?? undefined;
  const whatHappened =
    snap?.whatHappened ??
    // Through the shared describer, never raw: some runtime reasons are
    // deliberately machine tokens (`paused_by_owner`, ADR 0174) and this line is
    // read by an owner.
    describeRuntimeDisabledReason(snap?.runtimeDisabledReason) ??
    formatTransition({ from: runtime.payload?.from, to: state, reason: runtime.payload?.reason });
  return {
    id: row.definition.id,
    title: displayedTitle(row.definition, customSetupOptions(row.definition, opts)),
    severity,
    state,
    stateMeta: ALERT_STATE_META[state],
    lastTransitionAt: runtime.lastTransitionAt,
    whatHappened,
    occurrence: occurrenceLabel(runtime.occurrenceNumber),
    // Only honest while the alarm IS acked/silenced — the boat never clears
    // acknowledgeSource, so a re-fired Active row carries a stale human source.
    ackLine: isAckOrSilenceState(state)
      ? ackProvenance(snap?.acknowledgeSource, snap?.acknowledgeSourceNetworkId)
      : undefined,
    silenceUntil: silenceDeadline(snap),
    reactivationAt: reactivationDeadline(snap),
  };
}

/** The active band's rows, ordered most-urgent-then-most-recent. */
export function activeAlarmViews(
  response: AlarmStateResponse,
  opts: ConfiguredViewOptions = {}
): ActiveAlarmView[] {
  const views: ActiveAlarmView[] = [];
  for (const row of response.alerts) {
    const view = toActiveAlarmView(row, opts);
    if (view !== null) views.push(view);
  }
  return sortActiveForDisplay(views);
}

/** A fully-derived configured-alarm row (Band 2). */
export interface ConfiguredAlarmView {
  readonly id: string;
  readonly title: string;
  readonly severity?: AlarmSeverity;
  readonly enabled: boolean;
  readonly modeSummary: string;
  /**
   * The stored `enabledModes`, VERBATIM — the schedule the row's summary states
   * in preset words, and the set {@link alarmPausedNote} compares the boat's
   * current mode against. Carried rather than re-derived so the row and the
   * note cannot read the same definition two different ways.
   */
  readonly enabledModes: readonly string[];
  readonly compilePending: boolean;
  readonly compileReason?: string;
  /**
   * The definition has NEVER evaluated: compile-pending AND no runtime row at
   * all. The gear it needs has not reported since it was created — so it is not
   * a fault to chase, it is a wait, and the band demotes it into the quiet fold
   * instead of pricing it like a live row. A pending definition WITH a runtime
   * row is the other story (it used to evaluate and the signal went away) and
   * stays in its group wearing the Not-evaluating chip.
   */
  readonly awaitingEquipment: boolean;
  readonly runtimeDisabledReason?: string;
  /**
   * For a CUSTOM alarm: what it watches, in one plain-English line — including
   * its "— only while …" tail. `undefined` for a recipe alarm, and also when the
   * line would only repeat the title — the builder prefills the title FROM this
   * sentence, so most custom alarms would otherwise print it twice.
   */
  readonly customSentence?: string;
  /**
   * For a RECIPE alarm carrying conditions: `Only while the engine is running
   * above 300 rpm`. A custom alarm's conditions already ride
   * {@link customSentence} (or its title), so this stays `undefined` there and
   * the row never says it twice.
   */
  readonly conditionSummary?: string;
  /** The engine-running preset was dropped at compile time (plan D2). */
  readonly conditionUnbound: boolean;
  /**
   * The INSTANCE a pinned alarm watches, in human words — "Port engine oil
   * pressure". `undefined` when the alarm has no pin (it takes whichever
   * instance the boat reports, which is what the row already implies). The list
   * has no signal catalog, so the name is derived from the path alone.
   */
  readonly watching?: string;
}

/**
 * What a row view needs to state its numbers in the ACCOUNT's units (ADR 0189).
 *
 * BOTH are optional and absent means today's answer, byte for byte: a view built
 * with no options renders exactly as it did before per-user units, which is what
 * keeps the offline story above true when the catalog fetch has not landed.
 *
 * They are not interchangeable. A RECIPE alarm's thresholds carry their own
 * store unit in the shared threshold vocabulary, so `units` alone converts them.
 * A CUSTOM alarm's do not — its numbers are in whatever unit the boat declares
 * for the path it is pinned to, and `displayUnits`' store-unit gate declines
 * without that declaration. So a custom sentence needs `unitFor` as well, and
 * passing `units` on its own leaves it exactly as it was rather than converting
 * it wrongly.
 */
export interface ConfiguredViewOptions {
  /** The current inherited signal name from the boat's catalog. */
  readonly labelFor?: (path: string) => string;
  /** The account's units (`useUnits()`). */
  readonly units?: UnitSettings;
  /** A path's DECLARED store unit, from the signal catalog. */
  readonly unitFor?: (path: string) => string | null | undefined;
}

/** What {@link customAlarmSetup} takes, as a configured view resolves it. */
interface CustomSetupOptions {
  readonly labelFor?: (path: string) => string;
  readonly unit?: string;
  readonly units?: UnitSettings;
  readonly withoutConditions?: boolean;
}

/**
 * The {@link customAlarmSetup} options for one definition. The custom path's
 * declared unit is asked here because only the definition says which path the
 * row is about. NOT `opts.unitFor` itself: `customAlarmSetup`'s own `unitFor` is
 * for its CONDITION clause paths, and clauses stay in store units on every
 * surface.
 */
export function customSetupOptions(
  def: { readonly custom?: unknown },
  opts: ConfiguredViewOptions
): CustomSetupOptions {
  const customPath = readCustomSpec(def)?.path;
  const customUnit = customPath === undefined ? undefined : opts.unitFor?.(customPath);
  return {
    ...(opts.labelFor === undefined ? {} : { labelFor: opts.labelFor }),
    ...(customUnit === undefined || customUnit === null ? {} : { unit: customUnit }),
    ...(opts.units === undefined ? {} : { units: opts.units }),
  };
}

type CustomAlarmDef = Parameters<typeof customAlarmSetup>[0] & { readonly title: string };

/**
 * The options under which `def`'s title is its own custom sentence with the
 * stored numbers, or `undefined` when the title is anything else.
 *
 * The builder PREFILLS the title from the sentence, with the stored numbers
 * (`defaultTitle`, TOM-860). The prefill happened when the alarm was authored,
 * in the account's units, or in store units before ADR 0189, so both spellings
 * are tried. The builder's title stops at the trigger, so each is tried with
 * and without the "only while …" tail.
 */
function titleSentenceOptions(
  def: CustomAlarmDef,
  setupOpts: CustomSetupOptions
): CustomSetupOptions | undefined {
  const title = def.title.trim();
  const spellings = Object.keys(setupOpts).length > 0 ? [setupOpts, {}] : [{}];
  const tries = spellings.flatMap((o) => [o, { ...o, withoutConditions: true }]);
  return tries.find((o) => customAlarmSetup(def, { ...o, exact: true })?.sentence.trim() === title);
}

/**
 * The title an owner reads (TOM-860). A custom alarm's title is usually the
 * sentence the builder prefilled, stored with the exact numbers ("… goes above
 * 10.29 knots"). While it still is, it is shown rounded, in the same spelling
 * it was matched in ("… goes above 10 knots"). Any other title is shown as
 * stored. The stored title is never changed.
 */
export function displayedTitle(def: CustomAlarmDef, setupOpts: CustomSetupOptions = {}): string {
  const matched = titleSentenceOptions(def, setupOpts);
  if (matched === undefined) return def.title;
  return customAlarmSetup(def, matched)?.sentence ?? def.title;
}

/** Derive the configured-band view for one definition row. */
export function toConfiguredAlarmView(
  row: AlarmAlertRow,
  opts: ConfiguredViewOptions = {}
): ConfiguredAlarmView {
  const def = row.definition;
  const snap = runtimeSnapshot(row.runtime);
  const setupOpts = customSetupOptions(def, opts);
  const custom = customAlarmSetup(def, setupOpts);
  const sentence = custom?.sentence.trim();
  const title = def.title.trim();
  // The builder PREFILLS the title from this sentence, so most custom alarms
  // would print it twice. A title that is still that sentence is shown rounded
  // (`displayedTitle`) and the sentence under it is dropped. Without the
  // store-unit match, converting the sentence un-dedupes every row prefilled
  // before ADR 0189: the title reads "…drops below 20" and the line under it
  // "…drops below 66 ft", which is one alarm said twice in two units.
  const matched = titleSentenceOptions(def, setupOpts);
  // A title that stops at the trigger leaves the conditions to the sentence.
  const echoesTitle =
    sentence === title || (matched !== undefined && matched.withoutConditions !== true);
  const binding = alarmBindingSetup(def, { labelFor: opts.labelFor });
  return {
    id: def.id,
    title:
      matched === undefined ? def.title : (customAlarmSetup(def, matched)?.sentence ?? def.title),
    severity: def.severity ?? undefined,
    enabled: def.enabled,
    // A custom alarm's thresholds are reserved compiler keys, not recipe params:
    // summarizing them here would print `setpoint 26, set delay ms 5000`. The
    // sentence carries the numbers instead, so the summary keeps the modes only.
    modeSummary: custom
      ? formatWatchSummary(def.enabledModes, {}, opts.units)
      : formatWatchSummary(def.enabledModes, def.thresholdsByMode, opts.units),
    enabledModes: [...def.enabledModes],
    awaitingEquipment: def.compileStatus === 'pending' && row.runtime === null,
    customSentence:
      sentence !== undefined && sentence.length > 0 && !echoesTitle ? sentence : undefined,
    // Recipe alarms only: a custom alarm's conditions are already in its
    // sentence (and in the title the builder prefilled from it).
    conditionSummary: custom ? undefined : conditionSummaryLine(def, { labelFor: opts.labelFor }),
    conditionUnbound: readConditionUnbound(def),
    ...(binding === undefined ? {} : { watching: binding.label }),
    compilePending: def.compileStatus === 'pending',
    compileReason: def.compileReason ?? undefined,
    runtimeDisabledReason:
      typeof snap?.runtimeDisabledReason === 'string' &&
      snap.runtimeDisabledReason.trim().length > 0
        ? snap.runtimeDisabledReason
        : undefined,
  };
}

/**
 * The configured band's order: most-urgent severity first, then title. Exported
 * so the system groups sort their members with the SAME comparator the flat
 * list uses — grouping changes what sits next to what, never the order within.
 */
export function compareConfiguredViews(a: ConfiguredAlarmView, b: ConfiguredAlarmView): number {
  const rankA = a.severity ? severityRank(a.severity) : ALARM_SEVERITIES.length;
  const rankB = b.severity ? severityRank(b.severity) : ALARM_SEVERITIES.length;
  if (rankA !== rankB) return rankA - rankB;
  return a.title.localeCompare(b.title);
}

/** Every definition, ordered by severity rank then title (custom modes intact). */
export function configuredAlarmViews(
  response: AlarmStateResponse,
  opts: ConfiguredViewOptions = {}
): ConfiguredAlarmView[] {
  return response.alerts
    .map((row) => toConfiguredAlarmView(row, opts))
    .sort(compareConfiguredViews);
}

// ---------------------------------------------------------------------------
// SYSTEM GROUPS — filing the configured band by the part of the boat it
// watches (Phase 1 slice 4).
//
// A long flat list sorted by severity interleaves the bilge with the fridge,
// and the question an owner actually arrives with is "what is watching my
// engine?". The filing is a PRESENTATION concern and lives here, out of
// `toConfiguredAlarmView`, for the same reason the value lines do: a row view
// takes no samples, and the one catalog fact it does take
// (`ConfiguredViewOptions.unitFor`, a path's declared store unit) is OPTIONAL —
// with the catalog unfetched it degrades to the pre-ADR-0189 rendering rather
// than to a blank row. Filing a row needs the installer's channel LABEL, which
// has no such fallback: a group derived from a missing label would move rows
// between sections as the fetch lands.
//
// The recipe BASE id decides first, because it is the only thing that says what
// an alarm MEANS. `anchor_drift` and `gps_loss` watch navigation paths and are
// safety alarms; filing them by their path would bury the two alarms that wake
// you up under "Navigation". Only an alarm with no recipe of ours — a custom
// one, or a recipe from a newer build — falls through to its path.
//
// THE ONE REGEX. Core has role-classifier modules that guess what a switch
// channel is from its label; none of them is copied here, and none should be.
// The single allowance is the safety-word test below, which exists because a
// custom alarm on `electrical.switches.bank.1.3.state` is filed by its path as
// plumbing when the installer labelled it "Mid Bilge High Water" — the one case
// where the path is actively misleading about what the alarm is for.
// ---------------------------------------------------------------------------

/** The part of the boat an alarm watches — the configured band's filing. */
export type AlarmSystemGroup =
  | 'Safety'
  | 'Engine'
  | 'Power'
  | 'Tanks'
  | 'Navigation'
  | 'Cameras'
  | 'Comfort'
  | 'Other';

/** The groups in render order. `Other` is last: it is the catch-all. */
export const ALARM_SYSTEM_GROUPS: readonly AlarmSystemGroup[] = [
  'Safety',
  'Engine',
  'Power',
  'Tanks',
  'Navigation',
  'Cameras',
  'Comfort',
  'Other',
];

/** The UniFi Protect camera alarms (TOM-824): their own group on the page. */
const CAMERA_RECIPE_BASES: readonly string[] = [
  'camera_person',
  'camera_audio_alarm',
  'camera_offline',
];

/**
 * Which group each catalog recipe files under, by BASE id (a per-instance
 * `tank_high#tanks.fuel.0.currentLevel` parses to `tank_high` first). Every id
 * in `@neomaris/rules` RECIPE_SPECS appears exactly once; a recipe this build
 * has never heard of is not in the table and falls through to its path.
 */
const RECIPES_BY_GROUP: Readonly<Record<AlarmSystemGroup, readonly string[]>> = {
  // The life-safety recipes, shared with the cloud's Neo tools (cloud ADR 0210):
  // a change to one of these needs the owner's confirmation. The at-anchor depth
  // band (core ADR-146) is here beside anchor_drift, not beside shallow_depth:
  // those two watch depth paths but MEAN "you may be dragging".
  // The camera alarms file under Cameras (Mike, 2026-10-20), though two of them
  // are life-safety: the Neo confirmation rule reads the shared list, not the
  // heading, so it still holds for them.
  Safety: LIFE_SAFETY_RECIPE_BASES.filter((id) => !CAMERA_RECIPE_BASES.includes(id)),
  Engine: ['engine_overtemp', 'low_oil_pressure', 'charger_alternator_failure'],
  // The superseded `house_battery_*` pair files beside the per-bank pair that
  // replaced it: a boat mid-migration holds both, and they belong under the same
  // heading whichever recipe minted them.
  Power: [
    'house_battery_low',
    'house_battery_critical',
    'battery_low',
    'battery_critical',
    // TOM-318: the state-of-charge pair for a lithium bank.
    'battery_soc_low',
    'battery_soc_critical',
    'shore_power_lost',
  ],
  Tanks: ['tank_high', 'tank_fuel_low', 'tank_water_low', 'tank_waste_high'],
  Navigation: ['shallow_depth', 'gps_source_mismatch'],
  Cameras: CAMERA_RECIPE_BASES,
  Comfort: ['fridge_freezer_high_temp'],
  Other: ['gateway_offline', 'sensor_stale'],
};

const GROUP_BY_RECIPE_BASE: ReadonlyMap<string, AlarmSystemGroup> = new Map(
  ALARM_SYSTEM_GROUPS.flatMap((group) =>
    RECIPES_BY_GROUP[group].map((recipeId) => [recipeId, group] as const)
  )
);

/**
 * The safety words in an installer's channel label. THE one role regex in this
 * SPA — see the section note. Deliberately not extended: every word here names
 * a thing that floods, burns, or opens the boat to someone.
 */
const SAFETY_CHANNEL_RE = /bilge|high.?water|flood|smoke|gas|co|security|door|hatch/i;

/**
 * The path a def is FILED by when its recipe does not decide: the compiler's
 * resolved path first (what the boat actually watches), then the authored pin,
 * then a custom alarm's own path.
 *
 * Unlike {@link resolvedPathFor} this does NOT null out a pending definition.
 * Filing is not a claim that the alarm is armed — a pending bilge alarm belongs
 * under Safety, where its owner will look for it.
 */
function groupingPath(def: {
  readonly resolvedPath?: string | null;
  readonly binding?: unknown;
  readonly custom?: unknown;
}): string | undefined {
  const resolved = typeof def.resolvedPath === 'string' ? def.resolvedPath.trim() : '';
  if (resolved.length > 0) return resolved;
  return readBindingPath(def) ?? readCustomSpec(def)?.path;
}

/** File a telemetry path, given its channel label when the catalog has one. */
function groupForPath(path: string, channelLabel: string | null | undefined): AlarmSystemGroup {
  const seg = path.split('.').filter((s) => s.length > 0);
  switch (seg[0]) {
    case 'propulsion':
      return 'Engine';
    case 'electrical':
      // A switch channel is whatever the installer wired to it. Only its own
      // label can say, and when it says nothing this is not Power — an unlabelled
      // relay is not a claim about the boat's electrical system.
      if (seg[1] === 'switches') {
        return SAFETY_CHANNEL_RE.test(channelLabel ?? '') ? 'Safety' : 'Other';
      }
      return 'Power';
    case 'tanks':
      return 'Tanks';
    case 'navigation':
      return 'Navigation';
    case 'environment':
      if (seg[1] === 'depth') return 'Navigation';
      if (seg[1] === 'inside') return 'Comfort';
      return 'Other';
    default:
      return 'Other';
  }
}

/**
 * Which system group one definition files under. Pure: the channel label comes
 * from an injected lookup (the page already fetched the signal catalog for the
 * value lines), so this module still needs no catalog of its own.
 */
export function alarmSystemGroup(
  def: {
    readonly recipeId?: string;
    readonly resolvedPath?: string | null;
    readonly binding?: unknown;
    readonly custom?: unknown;
  },
  opts: { readonly channelLabelFor?: (path: string) => string | null | undefined } = {}
): AlarmSystemGroup {
  const base = parseRecipeId(def.recipeId ?? '').base;
  const byRecipe = GROUP_BY_RECIPE_BASE.get(base);
  if (byRecipe !== undefined) return byRecipe;
  const path = groupingPath(def);
  if (path === undefined) return 'Other';
  return groupForPath(path, opts.channelLabelFor?.(path));
}

/** Every definition's group, keyed by definition id — one call for the page. */
export function alarmSystemGroups(
  rows: readonly AlarmAlertRow[],
  opts: { readonly channelLabelFor?: (path: string) => string | null | undefined } = {}
): Map<string, AlarmSystemGroup> {
  const groups = new Map<string, AlarmSystemGroup>();
  for (const row of rows) {
    groups.set(row.definition.id, alarmSystemGroup(row.definition, opts));
  }
  return groups;
}

// ---------------------------------------------------------------------------
// PAUSED RIGHT NOW — the schedule, checked against the mode the boat reports.
// ---------------------------------------------------------------------------

/**
 * Why this alarm is not watching at this moment, or `undefined` when it is (or
 * when we cannot honestly say). Three rows never get the note:
 *
 *  - a DISABLED alarm — it is off in every mode, and "paused right now" would
 *    read like it comes back when the boat moves. Disabled beats paused.
 *  - a PENDING alarm — it watches nothing in any mode, so its schedule is not
 *    the reason it is quiet.
 *  - any row on a page that has not seen a `mode_change` frame. No frame, no
 *    claim: the alternative is guessing the boat's mode from its speed, which
 *    is exactly the guess the boat's own detector exists to make.
 *
 * TWO comparisons, because the boat runs neither of them the way the form
 * reads them.
 *
 *  1. THE GAP. The boat's evaluator arms a rule on a raw
 *     `enabledModes.includes(currentMode)` — no case fold, no Sailing fold. So
 *     a definition whose stored set is missing a mode the DETECTED preset
 *     implies is genuinely disarmed the moment the boat elects that mode, even
 *     though the tolerant fold below says otherwise. That is not hypothetical:
 *     every recipe alarm authored before Sailing was reachable stores
 *     `[Anchored, Docked, Maintenance, Moored, Underway]`, reads as "Always",
 *     and lost a real boat 7 of its 9 alarms on 2026-08-01 when its detector
 *     elected `Sailing` (`core/.../default-mode-rules.ts`,
 *     `core/tasks/sailing_mode_bug.md`).
 *
 *     The COMPILER now closes that specific hole — it appends `Sailing` to any
 *     rule enabled `Underway` (ADR 0175 / core ADR-109) — but the evaluator's
 *     gate itself is unchanged, and the fold only reaches a boat once it takes
 *     a core release carrying compiler v5 and boot-recompiles. This page cannot
 *     tell those boats apart (the `compiler_version` is not on the alarm read
 *     surface), and the note is only wrong in the safe direction: it warns
 *     about a gap an updated boat no longer has. So the sailing gap is still
 *     disclosed, and retiring it is a follow-up triggered by fleet core
 *     adoption (`cloud/tasks/alarm_sailing_fold.md`). The row has to SAY it, not mute it —
 *     so the gap (`watchSchedule.watchPresetGap`, the same set the authoring
 *     form discloses) is checked first, in the same words
 *     (`watchGapPhrase`). It gets its own sentence because the preset phrase
 *     would be nonsense here: the def reads as "Always", and "only watches
 *     whatever the boat is doing" explains nothing.
 *     A boat that has told us it is a POWER boat is the one exception, and it
 *     is exact rather than optimistic: its mode detector is filtered so it can
 *     never elect Sailing (ADR 0173 / core ADR-110), so the sailing gap cannot
 *     open on it and the disclosure is dropped. `vesselType` unknown — which is
 *     most of the fleet — keeps the cautious note, because "nobody has asked"
 *     is not evidence a boat has no sails.
 *  2. THE SCHEDULE. Otherwise the stored set is compared through
 *     `watchSchedule.canonicalWatchModes` — the same fold the authoring radio
 *     detects with — so a def that genuinely covers the mode (the full
 *     six-mode "Always" set, or `[Underway, Sailing]`) is never called paused.
 */
export function alarmPausedNote(
  view: {
    readonly enabled: boolean;
    readonly compilePending: boolean;
    readonly enabledModes: readonly string[];
  },
  currentMode: string | null | undefined,
  vesselType?: VesselType | null
): string | undefined {
  if (currentMode === null || currentMode === undefined || currentMode.trim().length === 0) {
    return undefined;
  }
  if (!view.enabled || view.compilePending) return undefined;
  const now = canonicalWatchModes([currentMode])[0];
  // A mode id this build cannot place is not evidence the alarm is paused.
  if (now === undefined) return undefined;
  // The mode as the boat spells it, canonicalized but NOT folded: the gap is
  // stated in the canonical universe, where Sailing is still Sailing.
  const nowExact = foldModesToCanonical([currentMode])[0];
  if (nowExact !== undefined && watchPresetGap(view.enabledModes, vesselType).includes(nowExact)) {
    return `Paused right now — doesn’t watch ${watchGapPhrase(nowExact)} yet (boat is ${formatModeLabel(currentMode)})`;
  }
  const watched = canonicalWatchModes(view.enabledModes);
  if (watched.length === 0 || watched.includes(now)) return undefined;
  return `Paused right now — only watches ${watchPhrase(view.enabledModes, vesselType)} (boat is ${formatModeLabel(currentMode)})`;
}

/**
 * The schedule as a phrase that reads inside a sentence. The presets lend their
 * own HINT ("under power or sail", or "under way" on a declared power boat)
 * rather than their label, because "only watches Only underway" is not a
 * sentence; a custom set keeps the TitleCase mode list the row's summary already
 * shows. Either way the words come from `watchSchedule`, so the note and the
 * form cannot describe one schedule differently — which is also why the vessel
 * type has to reach this far: the form's radio and this row are the same
 * sentence, seen twice.
 */
function watchPhrase(enabledModes: readonly string[], vesselType?: VesselType | null): string {
  const modes = presentModes(enabledModes);
  const preset = detectWatchPreset(modes);
  return watchPresets(vesselType).find((p) => p.id === preset)?.hint ?? modes.join(', ');
}

// ---------------------------------------------------------------------------
// WAITING FOR EQUIPMENT — the pending reason, in plain language.
// ---------------------------------------------------------------------------

/** The compiler's own reasons, restated for someone who owns a boat. */
const WAITING_REASON_TEXT: Readonly<Record<string, string>> = {
  'unknown recipeId': 'Your boat’s software doesn’t know this alarm type yet.',
  'no thresholds for any enabled mode': 'This alarm has no set point for the modes it watches.',
};

/**
 * The pending reason for a row in the waiting fold, in the owner's words.
 *
 * The compiler writes for the compiler — "boat reports no engine oil-pressure
 * path", "bound path not reported: propulsion.1.oilPressure". Those are exact
 * and they stay exact on the row that is still evaluating (the Not-evaluating
 * chip prints them verbatim, because a fault someone has to fix should name
 * the path). This fold is the other case: nothing is wrong, the gear simply
 * has not reported, and the line only has to say which gear.
 *
 * An unrecognised reason is sentence-cased and passed through rather than
 * replaced — a reason we cannot phrase is still worth reading.
 */
/**
 * A pending reason's subject, in a form fit to print.
 *
 * A dotted Signal K path goes through `signalLabel()` like the sibling arms do;
 * anything else is already prose the recipe wrote for a person and is left
 * exactly as written. Testing for the path shape rather than the arm it came
 * from is what keeps `signalLabel` off strings like "engine oil-pressure",
 * which it would mangle.
 */
function humanizeReasonSubject(subject: string): string {
  return /^[a-z][a-zA-Z0-9]*(?:\.[a-zA-Z0-9*]+)+$/.test(subject) ? signalLabel(subject) : subject;
}

export function waitingReasonText(reason: string | undefined): string {
  const trimmed = reason?.trim() ?? '';
  if (trimmed.length === 0) return 'Your boat hasn’t reported the signal this alarm needs yet.';
  const known = WAITING_REASON_TEXT[trimmed];
  if (known !== undefined) return known;
  const bound = /^bound path (?:not reported|does not fit this alarm)[:\s(]+(.+?)\)?$/.exec(
    trimmed
  );
  if (bound?.[1] !== undefined) {
    return trimmed.startsWith('bound path not reported')
      ? `Your boat isn’t reporting ${signalLabel(bound[1])} yet.`
      : `${signalLabel(bound[1])} isn’t a signal this alarm can watch.`;
  }
  const conditionPath = /^condition path not reported:\s*(.+)$/.exec(trimmed);
  if (conditionPath?.[1] !== undefined) {
    return `Your boat isn’t reporting ${signalLabel(conditionPath[1])}, which this alarm’s condition needs.`;
  }
  // `boat reports no <subject>` — where the subject is USUALLY a family phrase
  // the arm wrote for a person ("engine oil-pressure"), but historically could
  // also be a bare Signal K path. Two things went wrong with the old regex.
  //
  // Its tail group stripped a literal trailing `" path"` and nothing else, so
  // `"boat reports no electrical.shorePower.connected signal"` kept its last
  // word and the whole thing became the subject. And unlike both arms above it
  // never called `signalLabel()`, so a path capture stayed raw — an owner was
  // shown "Your boat isn't reporting electrical.shorePower.connected signal
  // yet."
  //
  // Those reasons are now authored as owner prose at source (`@neomaris/rules`),
  // so this arm should no longer meet a path at all. It is hardened anyway: a
  // boat running an older core still sends the old strings, and this is the
  // last place to catch one.
  const reports = /^boat (?:reports no|does not report) (.+?)(?:\s+(?:path|signal))?$/.exec(
    trimmed
  );
  if (reports?.[1] !== undefined) {
    return `Your boat isn’t reporting ${humanizeReasonSubject(reports[1])} yet.`;
  }
  // LEGACY pending-recipe reasons. The registry now authors these in the
  // owner's words at source ("Needs a smoke or CO detector connected to your
  // boat's network"), so a freshly compiled definition falls through to the
  // pass-through below and reads correctly with no mapping at all. These two
  // arms survive for rows compiled by an OLDER build and not yet recompiled —
  // `compile_reason` is stamped at compile time and sits in the column until the
  // next one, so on deploy day a boat's existing pending rows still carry the
  // engineering wording. Delete them once no such row can be in flight.
  if (/telemetry family and no boat-side producer$/.test(trimmed)) {
    return 'Nothing aboard reports this yet — connect the gear and this alarm starts watching.';
  }
  if (trimmed.startsWith('needs a first-class source-health signal')) {
    return 'Neomaris doesn’t produce this signal yet, so nothing can watch it.';
  }
  return `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)}.`;
}

// ---------------------------------------------------------------------------
// The configured band's LAYOUT: groups, the waiting fold, and the search.
// ---------------------------------------------------------------------------

/** The status dot beside a group heading. */
export type AlarmGroupTone = 'danger' | 'attention' | 'plain';

/** One rendered system group. */
export interface ConfiguredAlarmSection {
  readonly group: AlarmSystemGroup;
  /** Members, severity-then-title. Never empty — an empty group is dropped. */
  readonly rows: ConfiguredAlarmView[];
  /**
   * `danger` when a member is in the ACTIVE band right now, `attention` when a
   * member cannot evaluate, else `plain`. Read off the rows the group actually
   * shows, so a filtered group's dot describes what is under it.
   */
  readonly tone: AlarmGroupTone;
}

/** Everything the configured band renders, already filed and filtered. */
export interface ConfiguredBandLayout {
  /** Non-empty groups, in {@link ALARM_SYSTEM_GROUPS} order. */
  readonly sections: ConfiguredAlarmSection[];
  /** The never-evaluated definitions, demoted into the collapsed fold. */
  readonly waiting: ConfiguredAlarmView[];
  /** Rows in the band BEFORE the query — what decides if search is offered. */
  readonly total: number;
  /** Rows the query left visible, groups and fold together. */
  readonly shown: number;
}

/** Whether a row shows a Not-evaluating chip (drives the amber group dot). */
function notEvaluating(view: ConfiguredAlarmView): boolean {
  return view.compilePending || view.runtimeDisabledReason !== undefined;
}

/**
 * File, filter and order the configured band.
 *
 * The query is a plain case-insensitive substring over the row title and its
 * group name — no status facets, no fuzzy matching. Someone typing "bilge" or
 * "engine" into a list of forty alarms wants the obvious thing to happen, and
 * every clever matcher eventually hides a row the owner can see is there.
 */
export function configuredBandLayout(
  views: readonly ConfiguredAlarmView[],
  opts: {
    /** The group each row files under (the page derives it from the catalog). */
    readonly groupFor: (view: ConfiguredAlarmView) => AlarmSystemGroup;
    /** Ids currently SURFACED in the active band — the red dots. */
    readonly surfacedIds?: ReadonlySet<string>;
    /** The search box's text. Blank / absent ⇒ everything shows. */
    readonly query?: string;
  }
): ConfiguredBandLayout {
  const needle = (opts.query ?? '').trim().toLowerCase();
  const surfaced = opts.surfacedIds ?? new Set<string>();
  const matches = (view: ConfiguredAlarmView, group: AlarmSystemGroup): boolean =>
    needle.length === 0 || `${view.title} ${group}`.toLowerCase().includes(needle);

  const byGroup = new Map<AlarmSystemGroup, ConfiguredAlarmView[]>();
  const waiting: ConfiguredAlarmView[] = [];
  for (const view of views) {
    const group = opts.groupFor(view);
    if (!matches(view, group)) continue;
    if (view.awaitingEquipment) {
      waiting.push(view);
      continue;
    }
    const bucket = byGroup.get(group);
    if (bucket === undefined) byGroup.set(group, [view]);
    else bucket.push(view);
  }

  const sections: ConfiguredAlarmSection[] = [];
  for (const group of ALARM_SYSTEM_GROUPS) {
    const rows = byGroup.get(group);
    if (rows === undefined || rows.length === 0) continue;
    rows.sort(compareConfiguredViews);
    sections.push({
      group,
      rows,
      tone: rows.some((r) => surfaced.has(r.id))
        ? 'danger'
        : rows.some(notEvaluating)
          ? 'attention'
          : 'plain',
    });
  }
  waiting.sort(compareConfiguredViews);
  return {
    sections,
    waiting,
    total: views.length,
    shown: sections.reduce((n, s) => n + s.rows.length, 0) + waiting.length,
  };
}

/**
 * Whether the band is long enough to be worth searching. Under this many rows
 * the eye beats the keyboard, and an empty search box is one more control on a
 * page whose job is to be read at a glance.
 */
export const ALARM_SEARCH_MIN_ROWS = 8;

/** The "nothing matched" line, quoting what was typed back. */
export function alarmSearchEmptyText(query: string): string {
  return `Nothing matches “${query.trim()}”.`;
}

// ---------------------------------------------------------------------------
// Nav-badge summary (BoatShell rail + mobile bottom bar).
// ---------------------------------------------------------------------------

/** The nav-badge visual tone bucket. */
export type BadgeTone = 'danger' | 'attention' | 'neutral';

/**
 * Map the highest active severity to a badge tone: emergency/alarm → danger,
 * warning → attention, caution (or unknown) → neutral.
 */
export function severityBadgeTone(severity: AlarmSeverity | null | undefined): BadgeTone {
  switch (severity) {
    case 'emergency':
    case 'alarm':
      return 'danger';
    case 'warning':
      return 'attention';
    default:
      return 'neutral';
  }
}

/** The count + highest-severity summary the nav badge renders. */
export interface AlarmBadgeSummary {
  readonly activeCount: number;
  readonly highestActiveSeverity: AlarmSeverity | null;
  readonly tone: BadgeTone;
}

/**
 * Summarize a state response for the nav badge: how many rows are SURFACED
 * (runtime present + a surfaced state) and the most-urgent severity among them.
 */
export function summarizeAlerts(response: AlarmStateResponse): AlarmBadgeSummary {
  let activeCount = 0;
  let highest: AlarmSeverity | null = null;
  for (const row of response.alerts) {
    const runtime = row.runtime;
    if (runtime === null) continue;
    if (!isSurfacedState(asAlertState(runtime.state))) continue;
    activeCount += 1;
    const severity = row.definition.severity;
    if (severity !== null && (highest === null || severityRank(severity) < severityRank(highest))) {
      highest = severity;
    }
  }
  return { activeCount, highestActiveSeverity: highest, tone: severityBadgeTone(highest) };
}

// ---------------------------------------------------------------------------
// Ack / Silence command copy (UI-2) — the boat stays authoritative, so every
// message is honest about reachability; the tone mirrors revealStaging's
// `relayFailureMessage`.
// ---------------------------------------------------------------------------

/** The two owner alarm commands the web relays to the boat (§5.7). */
export type AlarmCommandAction = 'ack' | 'silence';

/**
 * The button label for a command. Silence NAMES its duration — "Silence for
 * 5 min" — because "Silence (temporary)" left the only question anyone has
 * ("temporary meaning what?") unanswered, and the answer is not the operator's
 * to choose: the period is stamped on the compiled rule from the severity
 * (`TEMPORARY_SILENCE_PERIOD_SECONDS_BY_SEVERITY`, ADR 086/098 — hence a named
 * duration rather than a duration picker). The table is IMPORTED, not mirrored:
 * a local copy would drift the moment a period is retuned and the button would
 * then lie about the buzzer.
 *
 * A row whose severity is absent or unrecognised keeps the old generic label —
 * naming a duration we cannot derive would be worse than not naming one.
 */
export function alarmCommandLabel(
  action: AlarmCommandAction,
  severity?: AlarmSeverity | null
): string {
  if (action === 'ack') return 'Acknowledge';
  const seconds =
    severity === undefined || severity === null
      ? undefined
      : TEMPORARY_SILENCE_PERIOD_SECONDS_BY_SEVERITY[severity];
  if (seconds === undefined || !Number.isFinite(seconds) || seconds <= 0) {
    return 'Silence (temporary)';
  }
  return `Silence for ${formatDuration(seconds * 1000)}`;
}

/** The in-flight (busy) label for a command's button. */
export function alarmCommandPendingLabel(action: AlarmCommandAction): string {
  return action === 'ack' ? 'Acknowledging…' : 'Silencing…';
}

/**
 * The honest banner shown when the relay 409s `boat_offline` — the cloud never
 * fabricates an acknowledged state, so it says plainly it couldn't reach the
 * boat. Matches `relayFailureMessage`'s "check that it's online" framing.
 */
export function alarmCommandOfflineMessage(action: AlarmCommandAction): string {
  return action === 'ack'
    ? 'We couldn’t reach your boat to acknowledge this alarm. Check that it’s online, then try again.'
    : 'We couldn’t reach your boat to silence this alarm. Check that it’s online, then try again.';
}

/** The generic honest failure line for any non-offline error (auth, 5xx, blip). */
export function alarmCommandErrorMessage(action: AlarmCommandAction): string {
  return action === 'ack'
    ? 'Something went wrong acknowledging this alarm. Please try again.'
    : 'Something went wrong silencing this alarm. Please try again.';
}

/** The tooltip on the disabled buttons when the boat presence reads offline. */
export function alarmCommandDisabledTitle(): string {
  return 'Your boat is offline right now — you can acknowledge or silence once it reconnects.';
}

// ---------------------------------------------------------------------------
// Alarm history rows (UI-2) — GET /v1/boats/:id/alerts/history.
// ---------------------------------------------------------------------------

/**
 * One `alert_state_history` entry as returned by the history read (§5.7).
 * `actor` is always `'boat:<id>'` (the boat is authoritative); the human actor —
 * who actually acknowledged — rides inside `payload.runtime` and is derived by
 * {@link historyActorLabel}, NOT read from `actor`.
 */
export interface AlarmHistoryEntry {
  readonly time: string;
  readonly alertId: string;
  readonly transition: string;
  readonly actor: string;
  readonly payload: AlarmRuntimePayload | null;
}

/** The `{ entries }` envelope of the history read. */
export interface AlarmHistoryResponse {
  readonly entries: AlarmHistoryEntry[];
}

/**
 * Derive the human actor for a history row. A human is attributed ONLY when the
 * row's landed transition is itself an `Acknowledged` / `Silenced` — then the
 * runtime snapshot's `acknowledgeSource` reads `cloud (alarms-verify)` (source,
 * with the network id parenthesised when present). This gate matters because the
 * boat evaluator never clears `acknowledgeSource`, so an automatic row after an
 * ack (e.g. a re-fire `Normal → Active`) still carries the stale human source;
 * reading it unconditionally would falsely attribute that automatic transition
 * to the human who last acked. Otherwise it falls back to the humanized
 * transition reason, and finally to `boat` — the transition happened on the boat
 * with no richer attribution. Pure.
 */
export function historyActorLabel(entry: AlarmHistoryEntry): string {
  if (isAckOrSilenceState(entry.transition)) {
    const snap = entry.payload?.runtime;
    const source = typeof snap?.acknowledgeSource === 'string' ? snap.acknowledgeSource.trim() : '';
    if (source.length > 0) {
      const netId =
        typeof snap?.acknowledgeSourceNetworkId === 'string'
          ? snap.acknowledgeSourceNetworkId.trim()
          : '';
      return netId.length > 0 ? `${source} (${netId})` : source;
    }
  }
  const reason = humanizeReason(entry.payload?.reason);
  return reason ?? 'boat';
}

/**
 * Format a history row's wall-clock time compactly (`Jul 23, 19:09`) in the
 * viewer's locale/zone. An unparseable timestamp falls back to the raw string so
 * a row is never blank. Not part of the parity block (web-only presentation).
 */
export function formatHistoryTime(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  return new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** A fully-derived history row for the timeline. */
export interface AlarmHistoryRowView {
  readonly alertId: string;
  /** `Active → Acknowledged` (or just the landed state when `from` is absent). */
  readonly transition: string;
  /** The human actor, e.g. `cloud (alarms-verify)` / `set delay fired` / `boat`. */
  readonly actor: string;
  /** Compact local time, e.g. `Jul 23, 19:09`. */
  readonly time: string;
}

/** Derive the timeline row view for one history entry. Pure. */
export function toHistoryRowView(entry: AlarmHistoryEntry): AlarmHistoryRowView {
  return {
    alertId: entry.alertId,
    transition: formatTransition({ from: entry.payload?.from, to: entry.transition }),
    actor: historyActorLabel(entry),
    time: formatHistoryTime(entry.time),
  };
}
